import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  costService,
  modelService,
  firmService,
  orderService,
  settingsService,
  reportService,
} from "../services/dataService";
import {
  ModelCost,
  Model,
  Firm,
  OrderStatus,
  Technic,
  Settings,
  PaginatedResult,
  OrderWithCostReportDto,
  ReportSummaryDto,
  MonthlyQuantityDto,
  MonthlyRevenueDto,
  CustomerRevenueDto,
  MonthlyProfitDto,
} from "../types";
import { formatCurrency, formatNumber, getImageUrl } from "../utils/formatters";
import {
  TechnicMatchMode,
  TECHNIC_MATCH_MODES,
  getTechnicMatchHint,
  matchesTechnicFilter,
} from "../utils/technicMatch";
import PageLoader from "../components/PageLoader";
import AlertModal from "../components/AlertModal";
import TechnicModal from "../components/TechnicModal";
import OrderCostsModal from "../components/OrderCostsModal";
import ImageManager from "../components/ImageManager";
import { useExchangeRates } from "../context/ExchangeRateContext";
import * as XLSX from "xlsx";
import {
  ResponsiveContainer,
  ComposedChart,
  BarChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import "./Reports.css";

type ReportTab = "table" | "monthly-quantity" | "monthly-revenue" | "customer-revenue" | "monthly-profit";

interface ModelCostReport {
  modelId: string;
  modelCode: string;
  modelName: string;
  firmCode: string;
  firmName: string;
  quantity: number;
  technics: string[];
  paintCount: number; // Boya adedi (PaintBased kalemlerin çeşit sayısı toplamı)
  costItems: any[]; // Backend'den gelen maliyet kalemleri
  totalAmount: number; // Toplam tutar (sipariş tutarı) - TL
  totalCost: number; // Maliyet - TL
  costWithOverhead: number; // Genel gider eklenmiş maliyet - TL
  finalCostWithProfit: number; // Kar marjı eklenmiş nihai maliyet - TL
  unitPrice: number; // Birim fiyat (orijinal döviz)
  unitPriceTRY: number; // Birim fiyat - TL
  unitCost: number; // Birim maliyet - TL
  suggestedPrice: number; // Hesaplanan Fiyat (kar marjı dahil) - TL
  currency: string;
  orderId: string;
  acceptanceDate: string; // Kabul tarihi
  completionDate: string; // Tamamlanma tarihi
}

interface CostDetailModal {
  isOpen: boolean;
  orderId: string;
  modelId: string;
  modelCode: string;
  firmCode: string;
  firmName: string;
  quantity: number;
  costs: ModelCost[];
  loading: boolean;
  totalCost: number; // Toplam maliyet (TRY)
  costWithOverhead: number; // Genel gider dahil (TRY)
  finalCostWithProfit: number; // Kar dahil (TRY)
}

const Reports: React.FC = () => {
  const navigate = useNavigate();
  const { usdRate, eurRate, gbpRate } = useExchangeRates();
  const [reportData, setReportData] = useState<ModelCostReport[]>([]);
  const [models, setModels] = useState<Model[]>([]);
  const [firms, setFirms] = useState<Firm[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedFirmId, setSelectedFirmId] = useState<string>("all");
  const [selectedModelId, setSelectedModelId] = useState<string>("all");
  const [sortField, setSortField] = useState<string>("completionDate");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [firmSearch, setFirmSearch] = useState<string>("");
  const [modelSearch, setModelSearch] = useState<string>("");
  const [firmDropdownOpen, setFirmDropdownOpen] = useState(false);
  const [modelDropdownOpen, setModelDropdownOpen] = useState(false);
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [expandedTechnics, setExpandedTechnics] = useState<Set<string>>(
    new Set(),
  );
  const [selectedTechnics, setSelectedTechnics] = useState<Technic[]>([]);
  const [technicMatchMode, setTechnicMatchMode] =
    useState<TechnicMatchMode>("exact");
  const [showTechnicModal, setShowTechnicModal] = useState(false);
  const [profitStatusFilter, setProfitStatusFilter] = useState<string>("all");
  // Boya adedi filtresi (PaintBased maliyet kalemlerinin çeşit sayısı toplamı)
  const [paintCountMin, setPaintCountMin] = useState<string>("");
  const [paintCountMax, setPaintCountMax] = useState<string>("");
  const [settings, setSettings] = useState<Settings | null>(null);
  const [summary, setSummary] = useState<ReportSummaryDto | null>(null);

  // Tab state
  const [activeTab, setActiveTab] = useState<ReportTab>("table");
  const [chartYear, setChartYear] = useState<number>(new Date().getFullYear());
  const [chartLoading, setChartLoading] = useState(false);
  const [monthlyQuantity, setMonthlyQuantity] = useState<MonthlyQuantityDto[]>([]);
  const [monthlyRevenue, setMonthlyRevenue] = useState<MonthlyRevenueDto[]>([]);
  const [customerRevenue, setCustomerRevenue] = useState<CustomerRevenueDto[]>([]);
  const [monthlyProfit, setMonthlyProfit] = useState<MonthlyProfitDto[]>([]);

  // Customer revenue extra filters
  const [custYear, setCustYear] = useState<number | "">(new Date().getFullYear());
  const [custMinRevenue, setCustMinRevenue] = useState<string>("");
  const [custMaxRevenue, setCustMaxRevenue] = useState<string>("");
  const [revenueConvertTRY, setRevenueConvertTRY] = useState<boolean>(false);
  const [custTopN, setCustTopN] = useState<number>(0);

  // Chart detail table sort state
  const [chartSortKey, setChartSortKey] = useState<string>("");
  const [chartSortDir, setChartSortDir] = useState<"asc" | "desc">("desc");

  const handleChartSort = (key: string) => {
    if (chartSortKey === key) {
      setChartSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setChartSortKey(key);
      setChartSortDir("desc");
    }
  };

  const sortedData = <T extends Record<string, any>>(data: T[]): T[] => {
    if (!chartSortKey) return data;
    return [...data].sort((a, b) => {
      const av = a[chartSortKey];
      const bv = b[chartSortKey];
      if (typeof av === "number" && typeof bv === "number") {
        return chartSortDir === "asc" ? av - bv : bv - av;
      }
      const as = String(av ?? "");
      const bs = String(bv ?? "");
      return chartSortDir === "asc" ? as.localeCompare(bs) : bs.localeCompare(as);
    });
  };

  const SortIcon = ({ col }: { col: string }) => (
    <span className={`sort-indicator ${chartSortKey === col ? "active" : ""}`}>
      {chartSortKey === col ? (chartSortDir === "asc" ? " ▲" : " ▼") : " ⇅"}
    </span>
  );

  // Infinite scroll state
  const [pageNumber, setPageNumber] = useState<number>(1);
  const pageNumberRef = useRef<number>(1); // Senkron takip için
  const [pageSize] = useState<number>(50);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [isLoadingAll, setIsLoadingAll] = useState<boolean>(false);

  // Alert Modal state
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "success" | "warning" | "error";
  }>({ isOpen: false, message: "", type: "info" });

  const [costDetailModal, setCostDetailModal] = useState<CostDetailModal>({
    isOpen: false,
    orderId: "",
    modelId: "",
    modelCode: "",
    firmCode: "",
    firmName: "",
    quantity: 0,
    costs: [],
    loading: false,
    totalCost: 0,
    costWithOverhead: 0,
    finalCostWithProfit: 0,
  });

  // Order Costs Modal state
  const [orderCostsModal, setOrderCostsModal] = useState<{
    isOpen: boolean;
    orderId: string;
    modelId: string;
  }>({ isOpen: false, orderId: "", modelId: "" });

  // Image state
  const [orderThumbnails, setOrderThumbnails] = useState<
    Record<string, string | null>
  >({});
  const [showImageManager, setShowImageManager] = useState(false);
  const [selectedOrderForImages, setSelectedOrderForImages] = useState<
    string | null
  >(null);

  // Sipariş resimleri thumbnail'lerini yükle
  const loadThumbnails = useCallback(
    async (orderIds: string[]) => {
      const idsToLoad = orderIds.filter((id) => !(id in orderThumbnails));
      if (idsToLoad.length === 0) return;

      // Batch olarak yükle (max 10 paralel)
      const batchSize = 10;
      for (let i = 0; i < idsToLoad.length; i += batchSize) {
        const batch = idsToLoad.slice(i, i + batchSize);
        const results = await Promise.allSettled(
          batch.map(async (orderId) => {
            const images = await orderService.getImages(orderId);
            return {
              orderId,
              imageUrl: images.length > 0 ? images[0].imageUrl : null,
            };
          }),
        );

        const newThumbnails: Record<string, string | null> = {};
        results.forEach((result) => {
          if (result.status === "fulfilled") {
            newThumbnails[result.value.orderId] = result.value.imageUrl;
          }
        });

        setOrderThumbnails((prev) => ({ ...prev, ...newThumbnails }));
      }
    },
    [orderThumbnails],
  );

  // reportData değiştiğinde thumbnail'leri yükle
  useEffect(() => {
    if (reportData.length > 0) {
      const orderIds = reportData.map((r) => r.orderId);
      loadThumbnails(orderIds);
    }
  }, [reportData]);

  useEffect(() => {
    loadData(true); // İlk yüklemede her zaman reset
  }, []);

  // Filtreler değiştiğinde data'yı sıfırdan yükle
  useEffect(() => {
    // İlk yüklemeden sonra filtre değişikliklerinde veri yükle
    // reportData boş olsa bile yükle (filtre değişikliğinde veri olabilir)
    const hasInitialLoad = models.length > 0 || firms.length > 0;
    if (hasInitialLoad) {
      loadData(true);
    }
  }, [selectedFirmId, selectedModelId, startDate, endDate]);

  // Maliyet kalemlerinden kaydedilmiş döviz kurunu çıkar (o günkü kur)
  const getSavedExchangeRate = (costItems: any[], currency: string): number | null => {
    if (!costItems || costItems.length === 0) return null;
    const first = costItems[0];
    switch (currency) {
      case "USD": return first.usdRate || null;
      case "EUR": return first.eurRate || null;
      case "GBP": return first.gbpRate || null;
      default: return null;
    }
  };

  // Birim fiyatı TRY'ye çevir: önce kaydedilmiş kur, yoksa canlı kur
  const convertPriceToTRY = (unitPrice: number, currency: string, costItems: any[]): number => {
    if (currency === "TRY" || currency === "TL") return unitPrice;
    const savedRate = getSavedExchangeRate(costItems, currency);
    if (savedRate) return unitPrice * savedRate;
    // Fallback: canlı kur
    if (currency === "USD" && usdRate) return unitPrice * usdRate;
    if (currency === "EUR" && eurRate) return unitPrice * eurRate;
    if (currency === "GBP" && gbpRate) return unitPrice * gbpRate;
    return 0;
  };

  // Dropdown dışına tıklandığında kapat
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest(".searchable-select")) {
        setFirmDropdownOpen(false);
        setModelDropdownOpen(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const loadData = async (reset: boolean = false) => {
    try {
      setLoading(true);

      const currentPage = reset ? 1 : pageNumber;

      const [
        modelsData,
        firmsData,
        settingsData,
        reportResponse,
        summaryResponse,
      ] = await Promise.all([
        modelService.getAll(),
        firmService.getFirms(),
        settingsService.getDefault().catch(() => null),
        reportService.getOrdersWithCosts(
          currentPage,
          pageSize,
          selectedFirmId,
          selectedModelId,
          startDate,
          endDate,
        ),
        reportService.getSummary(
          selectedFirmId,
          selectedModelId,
          startDate,
          endDate,
        ),
      ]);

      setSettings(settingsData);
      setModels(modelsData);
      setFirms(firmsData);
      setSummary(summaryResponse);

      // Daha fazla veri var mı kontrol et
      setHasMore(reportResponse.items.length === pageSize);

      if (reset) {
        setPageNumber(1);
        pageNumberRef.current = 1;
      }

      // Backend'den gelen veriyi frontend formatına dönüştür
      const reportItems: ModelCostReport[] = reportResponse.items.map(
        (item: OrderWithCostReportDto, index: number) => {
          const model = modelsData.find((m) => m.modelId === item.modelId);
          const firm = firmsData.find((f) => f.firmId === item.firmId);

          // Birim fiyat hesapla — maliyet kalemindeki o günkü kuru kullan
          const unitPrice = item.price || 0;
          const unitPriceTRY = convertPriceToTRY(unitPrice, item.priceCurrency, item.costItems || []);

          const totalAmountTRY = unitPriceTRY * item.quantity;

          // Backend'den gelen değerleri güvenli hale getir
          const totalCostTRY = item.totalCostTRY || 0;
          const costWithOverhead = item.costWithOverhead || 0;
          const finalCostWithProfit = item.finalCostWithProfit || 0;

          const technicsArray = item.technicNames || item.technics || [];

          return {
            modelId: item.modelId,
            modelCode: item.modelCode,
            modelName: item.modelName,
            firmCode: firm?.firmCode || "-",
            firmName: item.firmName,
            quantity: item.quantity,
            technics: technicsArray,
            paintCount: Number(item.paintCount ?? 0),
            costItems: item.costItems || [],
            totalAmount: totalAmountTRY,
            totalCost: totalCostTRY,
            costWithOverhead: costWithOverhead,
            finalCostWithProfit: finalCostWithProfit,
            unitPrice: unitPrice,
            unitPriceTRY: unitPriceTRY,
            unitCost:
              item.quantity > 0 && costWithOverhead > 0
                ? costWithOverhead / item.quantity
                : 0,
            suggestedPrice:
              item.quantity > 0 && finalCostWithProfit > 0
                ? finalCostWithProfit / item.quantity
                : 0,
            currency: item.priceCurrency,
            orderId: item.orderId,
            acceptanceDate: item.acceptanceDate,
            completionDate: item.completionDate || "",
          };
        },
      );

      if (reset) {
        setReportData(reportItems);
      } else {
        setReportData((prev) => {
          // Mevcut orderId'leri Set'e ekle
          const existingOrderIds = new Set(prev.map((item) => item.orderId));

          // Sadece yeni olan (duplicate olmayan) kayıtları ekle
          const newItems = reportItems.filter(
            (item) => !existingOrderIds.has(item.orderId),
          );

          return [...prev, ...newItems];
        });
      }
    } catch (error) {
      console.error("❌ Failed to load report data:", error);
      setAlertModal({
        isOpen: true,
        message: "Rapor verileri yüklenirken bir hata oluştu.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Tüm siparişleri yükle
  const loadAllOrders = async () => {
    try {
      setIsLoadingAll(true);

      // İlk sayfayı al ve toplam veriyi öğren
      const firstPageResponse = await reportService.getOrdersWithCosts(
        1,
        pageSize,
        selectedFirmId,
        selectedModelId,
        startDate,
        endDate,
      );

      const totalCount = firstPageResponse.totalCount || 0;
      const totalPages = Math.ceil(totalCount / pageSize);

      console.log(
        `📊 Toplam ${totalCount} kayıt, ${totalPages} sayfa yüklenecek...`,
      );

      // Tüm sayfaları paralel olarak yükle
      const pagePromises = [];
      for (let page = 1; page <= totalPages; page++) {
        pagePromises.push(
          reportService.getOrdersWithCosts(
            page,
            pageSize,
            selectedFirmId,
            selectedModelId,
            startDate,
            endDate,
          ),
        );
      }

      const allResponses = await Promise.all(pagePromises);

      // Tüm verileri birleştir
      const allItems = allResponses.flatMap((response) => response.items);

      // Backend'den gelen veriyi frontend formatına dönüştür
      const reportItems: ModelCostReport[] = allItems.map(
        (item: OrderWithCostReportDto) => {
          const model = models.find((m) => m.modelId === item.modelId);
          const firm = firms.find((f) => f.firmId === item.firmId);

          const unitPrice = item.price || 0;
          const unitPriceTRY = convertPriceToTRY(unitPrice, item.priceCurrency, item.costItems || []);

          const totalAmountTRY = unitPriceTRY * item.quantity;
          const totalCostTRY = item.totalCostTRY || 0;
          const costWithOverhead = item.costWithOverhead || 0;
          const finalCostWithProfit = item.finalCostWithProfit || 0;
          const technicsArray = item.technicNames || item.technics || [];

          return {
            modelId: item.modelId,
            modelCode: item.modelCode,
            modelName: item.modelName,
            firmCode: firm?.firmCode || "-",
            firmName: item.firmName,
            quantity: item.quantity,
            technics: technicsArray,
            paintCount: Number(item.paintCount ?? 0),
            costItems: item.costItems || [],
            totalAmount: totalAmountTRY,
            totalCost: totalCostTRY,
            costWithOverhead: costWithOverhead,
            finalCostWithProfit: finalCostWithProfit,
            unitPrice: unitPrice,
            unitPriceTRY: unitPriceTRY,
            unitCost:
              item.quantity > 0 && costWithOverhead > 0
                ? costWithOverhead / item.quantity
                : 0,
            suggestedPrice:
              item.quantity > 0 && finalCostWithProfit > 0
                ? finalCostWithProfit / item.quantity
                : 0,
            currency: item.priceCurrency,
            orderId: item.orderId,
            acceptanceDate: item.acceptanceDate,
            completionDate: item.completionDate || "",
          };
        },
      );

      setReportData(reportItems);
      setHasMore(false);

      setAlertModal({
        isOpen: true,
        message: `✅ ${allItems.length} sipariş başarıyla yüklendi!`,
        type: "success",
      });
    } catch (error) {
      console.error("❌ Tüm siparişler yüklenirken hata:", error);
      setAlertModal({
        isOpen: true,
        message: "Tüm siparişler yüklenirken bir hata oluştu.",
        type: "error",
      });
    } finally {
      setIsLoadingAll(false);
    }
  };

  // Daha fazla veri yükle
  const loadMore = useCallback(async () => {
    if (isLoadingMore || !hasMore) return;

    try {
      setIsLoadingMore(true);
      const nextPage = pageNumberRef.current + 1;

      const reportResponse = await reportService.getOrdersWithCosts(
        nextPage,
        pageSize,
        selectedFirmId,
        selectedModelId,
        startDate,
        endDate,
      );

      // Daha fazla veri var mı kontrol et
      setHasMore(reportResponse.items.length === pageSize);
      setPageNumber(nextPage);
      pageNumberRef.current = nextPage;

      // Backend'den gelen veriyi frontend formatına dönüştür
      const reportItems: ModelCostReport[] = reportResponse.items.map(
        (item: OrderWithCostReportDto, index: number) => {
          const model = models.find((m) => m.modelId === item.modelId);
          const firm = firms.find((f) => f.firmId === item.firmId);

          // Birim fiyat hesapla — maliyet kalemindeki o günkü kuru kullan
          const unitPrice = item.price || 0;
          const unitPriceTRY = convertPriceToTRY(unitPrice, item.priceCurrency, item.costItems || []);

          const totalAmountTRY = unitPriceTRY * item.quantity;

          // Backend'den gelen değerleri güvenli hale getir
          const totalCostTRY = item.totalCostTRY || 0;
          const costWithOverhead = item.costWithOverhead || 0;
          const finalCostWithProfit = item.finalCostWithProfit || 0;

          const technicsArray = item.technicNames || item.technics || [];

          return {
            modelId: item.modelId,
            modelCode: item.modelCode,
            modelName: item.modelName,
            firmCode: firm?.firmCode || "-",
            firmName: item.firmName,
            quantity: item.quantity,
            technics: technicsArray,
            paintCount: Number(item.paintCount ?? 0),
            costItems: item.costItems || [],
            totalAmount: totalAmountTRY,
            totalCost: totalCostTRY,
            costWithOverhead: costWithOverhead,
            finalCostWithProfit: finalCostWithProfit,
            unitPrice: unitPrice,
            unitPriceTRY: unitPriceTRY,
            unitCost:
              item.quantity > 0 && costWithOverhead > 0
                ? costWithOverhead / item.quantity
                : 0,
            suggestedPrice:
              item.quantity > 0 && finalCostWithProfit > 0
                ? finalCostWithProfit / item.quantity
                : 0,
            currency: item.priceCurrency,
            orderId: item.orderId,
            acceptanceDate: item.acceptanceDate,
            completionDate: item.completionDate || "",
          };
        },
      );

      setReportData((prev) => {
        // Mevcut orderId'leri Set'e ekle
        const existingOrderIds = new Set(prev.map((item) => item.orderId));

        // Sadece yeni olan (duplicate olmayan) kayıtları ekle
        const newItems = reportItems.filter(
          (item) => !existingOrderIds.has(item.orderId),
        );

        return [...prev, ...newItems];
      });
    } catch (error) {
      console.error("❌ Failed to load more data:", error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [
    isLoadingMore,
    hasMore,
    pageNumber,
    pageSize,
    models,
    firms,
    usdRate,
    eurRate,
    selectedFirmId,
    selectedModelId,
    startDate,
    endDate,
  ]);

  // Infinite scroll listener (loadMore'dan sonra olmalı)
  useEffect(() => {
    const handleScroll = () => {
      // Sayfa sonuna yaklaşıldığında kontrol et (500px kala)
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = document.documentElement.clientHeight;

      if (scrollTop + clientHeight >= scrollHeight - 500) {
        if (!isLoadingMore && hasMore && !loading) {
          loadMore();
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isLoadingMore, hasMore, loading, loadMore]);

  // Filtreleme (Sadece frontend filtreleri: teknik ve kar durumu)
  // Firma, model, tarih filtreleri backend'de uygulanıyor
  const getFilteredData = (): ModelCostReport[] => {
    let filtered = [...reportData];

    // Teknik filtresi - eşleşme moduna göre
    if (selectedTechnics.length > 0) {
      const selectedTechnicNames = selectedTechnics.map((t) => t.name);
      filtered = filtered.filter((r) =>
        matchesTechnicFilter(r.technics, selectedTechnicNames, technicMatchMode),
      );
    }

    // Boya adedi filtresi (Min–Max). Boş bırakılan uç sınırsız kabul edilir.
    const paintMin = paintCountMin.trim() === "" ? null : Number(paintCountMin);
    const paintMax = paintCountMax.trim() === "" ? null : Number(paintCountMax);
    const hasPaintMin = paintMin !== null && !Number.isNaN(paintMin);
    const hasPaintMax = paintMax !== null && !Number.isNaN(paintMax);

    if (hasPaintMin || hasPaintMax) {
      filtered = filtered.filter((r) => {
        const paintCount = r.paintCount ?? 0;
        if (hasPaintMin && paintCount < (paintMin as number)) return false;
        if (hasPaintMax && paintCount > (paintMax as number)) return false;
        return true;
      });
    }

    // Kar durumu filtresi
    if (profitStatusFilter !== "all") {
      filtered = filtered.filter((r) => {
        const status = getProfitStatus(r);
        return status === profitStatusFilter;
      });
    }

    // Sıralama
    filtered.sort((a, b) => {
      let aVal: any;
      let bVal: any;

      // Kar/Zarar sıralaması için özel hesaplama
      if (sortField === "profit") {
        aVal = a.totalAmount - a.costWithOverhead;
        bVal = b.totalAmount - b.costWithOverhead;
      } else {
        aVal = a[sortField as keyof ModelCostReport];
        bVal = b[sortField as keyof ModelCostReport];
      }

      if (typeof aVal === "string") aVal = aVal.toLowerCase();
      if (typeof bVal === "string") bVal = bVal.toLowerCase();

      if (sortDirection === "asc") {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    return filtered;
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const getSortIcon = (field: string) => {
    if (sortField !== field) return "↕";
    return sortDirection === "asc" ? "↑" : "↓";
  };

  // Detay modalını aç
  const openCostDetail = async (row: ModelCostReport) => {
    // Backend'den zaten maliyet kalemleri geldiği için API çağrısına gerek yok
    setCostDetailModal({
      isOpen: true,
      orderId: row.orderId,
      modelId: row.modelId,
      modelCode: row.modelCode,
      firmCode: row.firmCode,
      firmName: row.firmName,
      quantity: row.quantity,
      costs: row.costItems || [], // Zaten backend'den gelen veri
      loading: false,
      totalCost: row.totalCost, // Row seviyesinden toplamları al
      costWithOverhead: row.costWithOverhead,
      finalCostWithProfit: row.finalCostWithProfit,
    });
  };

  const closeCostDetail = () => {
    setCostDetailModal({
      isOpen: false,
      orderId: "",
      modelId: "",
      modelCode: "",
      firmCode: "",
      firmName: "",
      quantity: 0,
      costs: [],
      loading: false,
      totalCost: 0,
      costWithOverhead: 0,
      finalCostWithProfit: 0,
    });
  };

  // Maliyet detaylarında TL dönüşümü
  const convertCostToTRY = (cost: ModelCost): number => {
    const amount = cost.totalCost || 0;
    const currency = cost.currency || "TRY";
    if (currency === "TRY" || currency === "TL") return amount;
    if (currency === "USD" && cost.usdRate) return amount * cost.usdRate;
    if (currency === "EUR" && cost.eurRate) return amount * cost.eurRate;
    if (currency === "GBP" && cost.gbpRate) return amount * cost.gbpRate;
    return 0;
  };

  // costWithOverhead değerini TL'ye çevir
  const convertCostWithOverheadToTRY = (cost: ModelCost): number => {
    const amount = cost.costWithOverhead || 0;
    const currency = cost.currency || "TRY";
    if (currency === "TRY" || currency === "TL") return amount;
    if (currency === "USD" && cost.usdRate) return amount * cost.usdRate;
    if (currency === "EUR" && cost.eurRate) return amount * cost.eurRate;
    if (currency === "GBP" && cost.gbpRate) return amount * cost.gbpRate;
    return 0;
  };

  // finalCostWithProfit değerini TL'ye çevir
  const convertFinalCostToTRY = (cost: ModelCost): number => {
    const amount = cost.finalCostWithProfit || 0;
    const currency = cost.currency || "TRY";
    if (currency === "TRY" || currency === "TL") return amount;
    if (currency === "USD" && cost.usdRate) return amount * cost.usdRate;
    if (currency === "EUR" && cost.eurRate) return amount * cost.eurRate;
    if (currency === "GBP" && cost.gbpRate) return amount * cost.gbpRate;
    return 0;
  };

  // Kar oranı hesapla ve durumunu belirle
  const getProfitStatus = (
    row: ModelCostReport,
  ): "loss" | "below" | "above" => {
    const profit = row.totalAmount - row.costWithOverhead;
    const profitRate =
      row.costWithOverhead > 0 ? (profit / row.costWithOverhead) * 100 : 0;

    if (profit < 0) {
      return "loss"; // Zarar
    }

    const expectedRate = settings?.profitMargin || 40; // Varsayılan %40
    if (profitRate < expectedRate) {
      return "below"; // Beklenen altında
    }

    return "above"; // Beklenen üstünde
  };

  // Kar oranını yüzde olarak hesapla: (Net Kar / Maliyet) × 100
  const getProfitRate = (row: ModelCostReport): number => {
    if (row.costWithOverhead <= 0) return 0;
    const profit = row.totalAmount - row.costWithOverhead;
    return (profit / row.costWithOverhead) * 100;
  };

  // Teknik seçimi handler
  const handleTechnicSelect = (technic: Technic) => {
    const isAlreadySelected = selectedTechnics.some(
      (t) => t.technicId === technic.technicId,
    );

    if (isAlreadySelected) {
      // Zaten seçiliyse kaldır
      setSelectedTechnics(
        selectedTechnics.filter((t) => t.technicId !== technic.technicId),
      );
    } else {
      // Seçili değilse ekle
      setSelectedTechnics([...selectedTechnics, technic]);
    }
  };

  const handleClearTechnics = () => {
    setSelectedTechnics([]);
  };

  // Excel Export
  const handleExportExcel = () => {
    try {
      const data = filteredData;

      // Excel için veri hazırla
      const excelData: any[] = data.map((row, index) => {
        const profit = row.totalAmount - row.costWithOverhead;
        const profitRate =
          row.totalAmount > 0 ? (profit / row.totalAmount) * 100 : 0;
        const status = getProfitStatus(row);
        const statusText =
          status === "loss"
            ? "Zarar"
            : status === "below"
              ? "Hedef Altı"
              : "Hedef Üstü";

        return {
          Sıra: index + 1,
          Firma: row.firmName,
          "Model Kodu": row.modelCode,
          Adet: row.quantity,
          Teknikler: row.technics.join(", ") || "-",
          "Boya Adedi": row.paintCount,
          "Toplam Tutar (TL)": row.totalAmount,
          "Maliyet (TL)": row.totalCost,
          "Genel Gider Dahil (TL)": row.costWithOverhead,
          "Hesaplanan Tutar (TL)": row.finalCostWithProfit,
          "Birim Fiyat": row.unitPrice,
          Döviz: row.currency,
          "Birim Fiyat (TL)": row.unitPriceTRY,
          "Birim Maliyet (TL)": row.unitCost,
          "Hesaplanan Fiyat (TL)": row.suggestedPrice,
          "Kar/Zarar (TL)": profit,
          "Kar Oranı (%)": profitRate,
          "Kar Durumu": statusText,
          "Kabul Tarihi": row.acceptanceDate
            ? new Date(row.acceptanceDate).toLocaleDateString("tr-TR")
            : "-",
          "Tamamlanma Tarihi": row.completionDate
            ? new Date(row.completionDate).toLocaleDateString("tr-TR")
            : "-",
        };
      });

      // Özet satırları ekle
      excelData.push({});
      excelData.push({
        Sıra: "TOPLAM",
        Firma: "",
        "Model Kodu": "",
        Adet: totalQuantity,
        Teknikler: "",
        "Boya Adedi": "",
        "Toplam Tutar (TL)": totalAmount,
        "Maliyet (TL)": totalCost,
        "Genel Gider Dahil (TL)": totalCostWithOverhead,
        "Hesaplanan Tutar (TL)": totalFinalCost,
        "Birim Fiyat": "-",
        Döviz: "",
        "Birim Fiyat (TL)": "-",
        "Birim Maliyet (TL)": "-",
        "Hesaplanan Fiyat (TL)": "-",
        "Kar/Zarar (TL)": totalAmount - totalCostWithOverhead,
        "Kar Oranı (%)":
          totalCostWithOverhead > 0
            ? ((totalAmount - totalCostWithOverhead) / totalCostWithOverhead) *
              100
            : 0,
        "Kar Durumu": "",
        "Kabul Tarihi": "",
        "Tamamlanma Tarihi": "",
      });

      // Worksheet oluştur
      const ws = XLSX.utils.json_to_sheet(excelData);

      // Sütun genişliklerini ayarla
      const columnWidths = [
        { wch: 6 }, // Sıra
        { wch: 25 }, // Firma
        { wch: 15 }, // Model Kodu
        { wch: 10 }, // Adet
        { wch: 30 }, // Teknikler
        { wch: 12 }, // Boya Adedi
        { wch: 18 }, // Toplam Tutar
        { wch: 15 }, // Maliyet
        { wch: 20 }, // Genel Gider Dahil
        { wch: 20 }, // Hesaplanan Tutar
        { wch: 15 }, // Birim Fiyat
        { wch: 8 }, // Döviz
        { wch: 18 }, // Birim Fiyat TL
        { wch: 18 }, // Birim Maliyet
        { wch: 20 }, // Hesaplanan Fiyat
        { wch: 18 }, // Kar/Zarar
        { wch: 15 }, // Kar Oranı
        { wch: 15 }, // Kar Durumu
        { wch: 15 }, // Kabul Tarihi
        { wch: 18 }, // Tamamlanma Tarihi
      ];
      ws["!cols"] = columnWidths;

      // Workbook oluştur
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Rapor");

      // Dosya adı oluştur
      const today = new Date();
      const dateStr = today.toISOString().split("T")[0];
      const fileName = `Siparis_Raporu_${dateStr}.xlsx`;

      // Excel dosyasını indir
      XLSX.writeFile(wb, fileName);

      setAlertModal({
        isOpen: true,
        message: "Excel dosyası başarıyla indirildi!",
        type: "success",
      });
    } catch (error) {
      console.error("Excel export hatası:", error);
      setAlertModal({
        isOpen: true,
        message: "Excel dosyası oluşturulurken bir hata oluştu.",
        type: "error",
      });
    }
  };

  // Toplamlar
  const filteredData = getFilteredData();

  // Frontend filtreleri (teknik, boya adedi veya kar durumu) aktifse, filteredData'dan hesapla
  // Değilse backend'den gelen summary'i kullan
  const hasFrontendFilters =
    selectedTechnics.length > 0 ||
    profitStatusFilter !== "all" ||
    paintCountMin.trim() !== "" ||
    paintCountMax.trim() !== "";

  const totalQuantity = hasFrontendFilters
    ? filteredData.reduce((sum, r) => sum + r.quantity, 0)
    : summary?.totalQuantity || 0;

  const totalAmount = hasFrontendFilters
    ? filteredData.reduce((sum, r) => sum + r.totalAmount, 0)
    : summary?.totalAmount || 0;

  const totalCost = hasFrontendFilters
    ? filteredData.reduce((sum, r) => sum + r.totalCost, 0)
    : summary?.totalCost || 0;

  const totalCostWithOverhead = hasFrontendFilters
    ? filteredData.reduce((sum, r) => sum + r.costWithOverhead, 0)
    : summary?.totalCostWithOverhead || 0;

  const totalFinalCost = hasFrontendFilters
    ? filteredData.reduce((sum, r) => sum + r.finalCostWithProfit, 0)
    : summary?.totalFinalCost || 0;

  // Chart data yükleme
  const MONTH_NAMES = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];

  const loadChartData = async (tab: ReportTab) => {
    setChartLoading(true);
    try {
      const firmParam = selectedFirmId === "all" ? undefined : selectedFirmId;
      if (tab === "monthly-quantity") {
        const data = await reportService.getMonthlyQuantity(chartYear, firmParam);
        setMonthlyQuantity(data.sort((a: any, b: any) => a.month - b.month).map((d: any) => ({ ...d, monthName: MONTH_NAMES[d.month - 1] || `Ay ${d.month}` })));
      } else if (tab === "monthly-revenue") {
        const data = await reportService.getMonthlyRevenue(chartYear, firmParam);
        setMonthlyRevenue(data.sort((a: any, b: any) => a.month - b.month).map((d: any) => ({ ...d, monthName: MONTH_NAMES[d.month - 1] || `Ay ${d.month}` })));
      } else if (tab === "customer-revenue") {
        let sd = startDate || undefined;
        let ed = endDate || undefined;
        if (custYear && !startDate && !endDate) {
          sd = `${custYear}-01-01`;
          ed = `${custYear}-12-31`;
        }
        const data = await reportService.getCustomerRevenue(sd, ed);
        setCustomerRevenue(data);
      } else if (tab === "monthly-profit") {
        const data = await reportService.getMonthlyProfit(chartYear, firmParam);
        setMonthlyProfit(data.sort((a: any, b: any) => a.month - b.month).map((d: any) => ({ ...d, monthName: MONTH_NAMES[d.month - 1] || `Ay ${d.month}` })));
      }
    } catch (err) {
      console.error("Chart data loading error:", err);
    } finally {
      setChartLoading(false);
    }
  };

  // Tab değiştiğinde veya yıl/filtre değiştiğinde chart data yükle
  useEffect(() => {
    if (activeTab !== "table") {
      loadChartData(activeTab);
    }
    setChartSortKey("");
    setChartSortDir("desc");
  }, [activeTab, chartYear, selectedFirmId, startDate, endDate, custYear]);

  // Yıllar listesi (2020-aktif yıl)
  const availableYears = Array.from({ length: new Date().getFullYear() - 2019 }, (_, i) => 2020 + i).reverse();

  const PIE_COLORS = ["#4338ca", "#1d4ed8", "#6d28d9", "#0f766e", "#b45309", "#dc2626", "#7c3aed", "#0891b2", "#059669", "#d97706"];

  // Filtered customer revenue
  const filteredCustomerRevenue = (() => {
    let data = [...customerRevenue];
    // Minimum revenue threshold
    const minRev = Number(custMinRevenue);
    if (minRev > 0) data = data.filter(r => r.totalRevenueTRY >= minRev);
    // Maximum revenue threshold
    const maxRev = Number(custMaxRevenue);
    if (maxRev > 0) data = data.filter(r => r.totalRevenueTRY <= maxRev);
    // Top N
    if (custTopN > 0) data = data.slice(0, custTopN);
    return data;
  })();

  if (loading && activeTab === "table") {
    return <PageLoader message="Rapor yükleniyor..." />;
  }

  return (
    <div className="reports-container">
      {/* Tab Navigation */}
      <div className="report-tabs">
        <button className={`report-tab ${activeTab === "table" ? "active" : ""}`} onClick={() => setActiveTab("table")}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="3" y1="15" x2="21" y2="15"/><line x1="9" y1="3" x2="9" y2="21"/></svg>
          Sipariş Tablosu
        </button>
        <button className={`report-tab ${activeTab === "monthly-quantity" ? "active" : ""}`} onClick={() => setActiveTab("monthly-quantity")}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="12" width="4" height="9"/><rect x="10" y="6" width="4" height="15"/><rect x="17" y="2" width="4" height="19"/></svg>
          Aylık Adet
        </button>
        <button className={`report-tab ${activeTab === "monthly-revenue" ? "active" : ""}`} onClick={() => setActiveTab("monthly-revenue")}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="22,12 18,12 15,21 9,3 6,12 2,12"/></svg>
          Aylık Ciro
        </button>
        <button className={`report-tab ${activeTab === "customer-revenue" ? "active" : ""}`} onClick={() => setActiveTab("customer-revenue")}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
          Müşteri Ciro
        </button>
        <button className={`report-tab ${activeTab === "monthly-profit" ? "active" : ""}`} onClick={() => setActiveTab("monthly-profit")}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
          Aylık Kâr
        </button>
      </div>

      {/* Chart Reports */}
      {activeTab !== "table" && (
        <div className="chart-report-container">
          <div className="chart-filter-bar">
            {activeTab !== "customer-revenue" && (
              <div className="chart-filter-group">
                <label>Yıl</label>
                <select value={chartYear} onChange={(e) => setChartYear(Number(e.target.value))}>
                  {availableYears.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
            )}
            {activeTab !== "customer-revenue" && (
              <div className="chart-filter-group">
                <label>Firma</label>
                <select value={selectedFirmId} onChange={(e) => setSelectedFirmId(e.target.value)}>
                  <option value="all">Tüm Firmalar</option>
                  {firms.sort((a, b) => a.firmName.localeCompare(b.firmName)).map((f) => (
                    <option key={f.firmId} value={f.firmId}>{f.firmName}</option>
                  ))}
                </select>
              </div>
            )}
            {activeTab === "monthly-revenue" && (
              <div className="chart-filter-group" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <label style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
                  <input type="checkbox" checked={revenueConvertTRY} onChange={(e) => setRevenueConvertTRY(e.target.checked)} style={{ width: 16, height: 16, accentColor: "#4338ca" }} />
                  Dövizi TL'ye Çevir
                </label>
              </div>
            )}
            {activeTab === "customer-revenue" && (
              <>
                <div className="chart-filter-group">
                  <label>Yıl</label>
                  <select value={custYear} onChange={(e) => { const v = e.target.value; setCustYear(v === "" ? "" : Number(v)); setStartDate(""); setEndDate(""); }}>
                    {availableYears.map((y) => <option key={y} value={y}>{y}</option>)}
                    <option value="">Tümü</option>
                  </select>
                </div>
                <div className="chart-filter-group">
                  <label>Başlangıç</label>
                  <input type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); if (e.target.value) setCustYear(""); }} />
                </div>
                <div className="chart-filter-group">
                  <label>Bitiş</label>
                  <input type="date" value={endDate} onChange={(e) => { setEndDate(e.target.value); if (e.target.value) setCustYear(""); }} />
                </div>
                <div className="chart-filter-group">
                  <label>Min. Ciro (₺)</label>
                  <input type="number" placeholder="0" value={custMinRevenue} onChange={(e) => setCustMinRevenue(e.target.value)} style={{ width: 110 }} />
                </div>
                <div className="chart-filter-group">
                  <label>Max. Ciro (₺)</label>
                  <input type="number" placeholder="0" value={custMaxRevenue} onChange={(e) => setCustMaxRevenue(e.target.value)} style={{ width: 110 }} />
                </div>
                <div className="chart-filter-group">
                  <label>Top N</label>
                  <select value={custTopN} onChange={(e) => setCustTopN(Number(e.target.value))}>
                    <option value={0}>Tümü</option>
                    <option value={5}>Top 5</option>
                    <option value={10}>Top 10</option>
                    <option value={20}>Top 20</option>
                  </select>
                </div>
              </>
            )}
          </div>

          {chartLoading ? (
            <PageLoader message="Grafik verileri yükleniyor..." />
          ) : (
            <>
              {/* AYLIK ADET */}
              {activeTab === "monthly-quantity" && (
                <div className="chart-section">
                  <div className="chart-kpi-row">
                    <div className="chart-kpi-card" style={{ borderLeftColor: "#4338ca" }}>
                      <span className="chart-kpi-value">{formatNumber(monthlyQuantity.reduce((s, r) => s + r.orderCount, 0), 0)}</span>
                      <span className="chart-kpi-label">Toplam Sipariş</span>
                    </div>
                    <div className="chart-kpi-card" style={{ borderLeftColor: "#0f766e" }}>
                      <span className="chart-kpi-value">{formatNumber(monthlyQuantity.reduce((s, r) => s + r.totalQuantity, 0))}</span>
                      <span className="chart-kpi-label">Toplam Miktar</span>
                    </div>
                    <div className="chart-kpi-card" style={{ borderLeftColor: "#b45309" }}>
                      <span className="chart-kpi-value">{formatNumber(monthlyQuantity.reduce((s, r) => s + r.totalPieceCount, 0), 0)}</span>
                      <span className="chart-kpi-label">Toplam Parça</span>
                    </div>
                  </div>
                  <div className="chart-tile">
                    <h3>Aylık Sipariş Adedi ve Miktar — {chartYear}</h3>
                    <ResponsiveContainer width="100%" height={400}>
                      <ComposedChart data={monthlyQuantity} margin={{ top: 10, right: 30, left: 10, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                        <XAxis dataKey="monthName" tick={{ fontSize: 12 }} />
                        <YAxis yAxisId="left" tick={{ fontSize: 11 }} tickFormatter={(v) => formatNumber(v, 0)} />
                        <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} tickFormatter={(v) => formatNumber(v, 0)} />
                        <Tooltip formatter={(v: any) => formatNumber(Number(v), 0)} />
                        <Legend />
                        <Bar yAxisId="left" dataKey="orderCount" name="Sipariş Adedi" fill="#4338ca" radius={[4, 4, 0, 0]} />
                        <Bar yAxisId="left" dataKey="totalPieceCount" name="Parça Sayısı" fill="#b45309" radius={[4, 4, 0, 0]} />
                        <Line yAxisId="right" type="monotone" dataKey="totalQuantity" name="Toplam Miktar" stroke="#0f766e" strokeWidth={2.5} dot={{ r: 4 }} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="chart-tile">
                    <h3>Detay Tablosu</h3>
                    <table className="chart-detail-table">
                      <thead>
                        <tr>
                          <th className="sortable-th" onClick={() => handleChartSort("monthName")}>Ay<SortIcon col="monthName" /></th>
                          <th className="text-right sortable-th" onClick={() => handleChartSort("orderCount")}>Sipariş Adedi<SortIcon col="orderCount" /></th>
                          <th className="text-right sortable-th" onClick={() => handleChartSort("totalQuantity")}>Toplam Miktar<SortIcon col="totalQuantity" /></th>
                          <th className="text-right sortable-th" onClick={() => handleChartSort("totalPieceCount")}>Parça Sayısı<SortIcon col="totalPieceCount" /></th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedData(monthlyQuantity).map((r) => (
                          <tr key={`${r.year}-${r.month}`}>
                            <td>{r.monthName}</td>
                            <td className="text-right">{formatNumber(r.orderCount, 0)}</td>
                            <td className="text-right">{formatNumber(r.totalQuantity)}</td>
                            <td className="text-right">{formatNumber(r.totalPieceCount, 0)}</td>
                          </tr>
                        ))}
                        {monthlyQuantity.length === 0 && <tr><td colSpan={4} style={{ textAlign: "center", color: "#999" }}>Veri bulunamadı</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* AYLIK CİRO */}
              {activeTab === "monthly-revenue" && (
                <div className="chart-section">
                  <div className="chart-kpi-row">
                    <div className="chart-kpi-card" style={{ borderLeftColor: "#4338ca" }}>
                      <span className="chart-kpi-value">{formatCurrency(monthlyRevenue.reduce((s, r) => s + r.totalRevenueTRY, 0), "TRY")}</span>
                      <span className="chart-kpi-label">Toplam Ciro (TRY)</span>
                    </div>
                    {!revenueConvertTRY && (
                      <>
                        <div className="chart-kpi-card" style={{ borderLeftColor: "#1d4ed8" }}>
                          <span className="chart-kpi-value">{formatCurrency(monthlyRevenue.reduce((s, r) => s + r.revenueUSD, 0), "USD")}</span>
                          <span className="chart-kpi-label">USD Ciro</span>
                        </div>
                        <div className="chart-kpi-card" style={{ borderLeftColor: "#6d28d9" }}>
                          <span className="chart-kpi-value">{formatCurrency(monthlyRevenue.reduce((s, r) => s + r.revenueEUR, 0), "EUR")}</span>
                          <span className="chart-kpi-label">EUR Ciro</span>
                        </div>
                      </>
                    )}
                    <div className="chart-kpi-card" style={{ borderLeftColor: "#0f766e" }}>
                      <span className="chart-kpi-value">{formatNumber(monthlyRevenue.reduce((s, r) => s + r.orderCount, 0), 0)}</span>
                      <span className="chart-kpi-label">Toplam Sipariş</span>
                    </div>
                  </div>
                  <div className="chart-tile">
                    <h3>{revenueConvertTRY ? "Aylık Toplam Ciro (TRY)" : "Aylık Ciro Dağılımı"} — {chartYear}</h3>
                    <ResponsiveContainer width="100%" height={400}>
                      <BarChart data={monthlyRevenue} margin={{ top: 10, right: 30, left: 10, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                        <XAxis dataKey="monthName" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => formatNumber(v)} />
                        <Tooltip formatter={(v: any) => formatNumber(Number(v))} />
                        <Legend />
                        {revenueConvertTRY ? (
                          <Bar dataKey="totalRevenueTRY" name="Toplam TRY Ciro" fill="#4338ca" radius={[4, 4, 0, 0]} />
                        ) : (
                          <>
                            <Bar dataKey="revenueTRY" name="TRY Ciro" fill="#4338ca" radius={[4, 4, 0, 0]} stackId="a" />
                            <Bar dataKey="revenueUSD" name="USD Ciro (Orijinal)" fill="#1d4ed8" radius={[4, 4, 0, 0]} stackId="b" />
                            <Bar dataKey="revenueEUR" name="EUR Ciro (Orijinal)" fill="#6d28d9" radius={[4, 4, 0, 0]} stackId="c" />
                          </>
                        )}
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="chart-tile">
                    <h3>Toplam TRY Ciro Trendi — {chartYear}</h3>
                    <ResponsiveContainer width="100%" height={350}>
                      <AreaChart data={monthlyRevenue} margin={{ top: 10, right: 30, left: 10, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                        <XAxis dataKey="monthName" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => formatNumber(v)} />
                        <Tooltip formatter={(v: any) => formatNumber(Number(v))} />
                        <Area type="monotone" dataKey="totalRevenueTRY" name="Toplam TRY Ciro" stroke="#4338ca" fill="#4338ca" fillOpacity={0.1} strokeWidth={2.5} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="chart-tile">
                    <h3>Detay Tablosu</h3>
                    <table className="chart-detail-table">
                      <thead>
                        <tr>
                          <th className="sortable-th" onClick={() => handleChartSort("monthName")}>Ay<SortIcon col="monthName" /></th>
                          <th className="text-right sortable-th" onClick={() => handleChartSort("orderCount")}>Sipariş<SortIcon col="orderCount" /></th>
                          <th className="text-right sortable-th" onClick={() => handleChartSort("totalQuantity")}>Miktar<SortIcon col="totalQuantity" /></th>
                          {!revenueConvertTRY && (
                            <>
                              <th className="text-right sortable-th" onClick={() => handleChartSort("revenueTRY")}>TRY Ciro<SortIcon col="revenueTRY" /></th>
                              <th className="text-right sortable-th" onClick={() => handleChartSort("revenueUSD")}>USD Ciro<SortIcon col="revenueUSD" /></th>
                              <th className="text-right sortable-th" onClick={() => handleChartSort("revenueEUR")}>EUR Ciro<SortIcon col="revenueEUR" /></th>
                            </>
                          )}
                          <th className="text-right sortable-th" onClick={() => handleChartSort("totalRevenueTRY")}>Toplam TRY<SortIcon col="totalRevenueTRY" /></th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedData(monthlyRevenue).map((r) => (
                          <tr key={`${r.year}-${r.month}`}>
                            <td>{r.monthName}</td>
                            <td className="text-right">{formatNumber(r.orderCount, 0)}</td>
                            <td className="text-right">{formatNumber(r.totalQuantity)}</td>
                            {!revenueConvertTRY && (
                              <>
                                <td className="text-right">{formatNumber(r.revenueTRY)}</td>
                                <td className="text-right">{formatNumber(r.revenueUSD)}</td>
                                <td className="text-right">{formatNumber(r.revenueEUR)}</td>
                              </>
                            )}
                            <td className="text-right" style={{ fontWeight: 600 }}>{formatNumber(r.totalRevenueTRY)}</td>
                          </tr>
                        ))}
                        {monthlyRevenue.length === 0 && <tr><td colSpan={revenueConvertTRY ? 4 : 7} style={{ textAlign: "center", color: "#999" }}>Veri bulunamadı</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* MÜŞTERİ BAZLI CİRO */}
              {activeTab === "customer-revenue" && (
                <div className="chart-section">
                  <div className="chart-kpi-row">
                    <div className="chart-kpi-card" style={{ borderLeftColor: "#4338ca" }}>
                      <span className="chart-kpi-value">{formatCurrency(filteredCustomerRevenue.reduce((s, r) => s + r.totalRevenueTRY, 0), "TRY")}</span>
                      <span className="chart-kpi-label">Toplam Ciro (TRY)</span>
                    </div>
                    <div className="chart-kpi-card" style={{ borderLeftColor: "#1d4ed8" }}>
                      <span className="chart-kpi-value">{formatCurrency(filteredCustomerRevenue.reduce((s, r) => s + r.revenueUSD, 0), "USD")}</span>
                      <span className="chart-kpi-label">USD Ciro</span>
                    </div>
                    <div className="chart-kpi-card" style={{ borderLeftColor: "#6d28d9" }}>
                      <span className="chart-kpi-value">{formatCurrency(filteredCustomerRevenue.reduce((s, r) => s + r.revenueEUR, 0), "EUR")}</span>
                      <span className="chart-kpi-label">EUR Ciro</span>
                    </div>
                    <div className="chart-kpi-card" style={{ borderLeftColor: "#0f766e" }}>
                      <span className="chart-kpi-value">{formatNumber(filteredCustomerRevenue.reduce((s, r) => s + r.orderCount, 0), 0)}</span>
                      <span className="chart-kpi-label">Toplam Sipariş</span>
                    </div>
                    <div className="chart-kpi-card" style={{ borderLeftColor: "#b45309" }}>
                      <span className="chart-kpi-value">{filteredCustomerRevenue.length}{customerRevenue.length !== filteredCustomerRevenue.length ? ` / ${customerRevenue.length}` : ""}</span>
                      <span className="chart-kpi-label">Müşteri Sayısı</span>
                    </div>
                  </div>
                  <div className="chart-tile">
                    <h3>Müşteri Bazlı Ciro (TRY)</h3>
                    <ResponsiveContainer width="100%" height={Math.max(400, filteredCustomerRevenue.length * 40)}>
                      <BarChart data={filteredCustomerRevenue} layout="vertical" margin={{ top: 10, right: 30, left: 120, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                        <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => formatNumber(v)} />
                        <YAxis type="category" dataKey="firmName" tick={{ fontSize: 11 }} width={110} />
                        <Tooltip formatter={(v: any) => formatNumber(Number(v))} />
                        <Bar dataKey="totalRevenueTRY" name="Toplam TRY Ciro" fill="#4338ca" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="chart-tile">
                    <h3>Müşteri Ciro Dağılımı</h3>
                    <ResponsiveContainer width="100%" height={400}>
                      <PieChart>
                        <Pie data={filteredCustomerRevenue} dataKey="totalRevenueTRY" nameKey="firmName" cx="50%" cy="50%" outerRadius={150} label={(entry: any) => entry.firmName}>
                          {filteredCustomerRevenue.map((_, i) => (
                            <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(v: any) => formatNumber(Number(v))} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="chart-tile">
                    <h3>Detay Tablosu{filteredCustomerRevenue.length !== customerRevenue.length ? ` (${filteredCustomerRevenue.length} / ${customerRevenue.length} müşteri)` : ""}</h3>
                    <table className="chart-detail-table">
                      <thead>
                        <tr>
                          <th className="sortable-th" onClick={() => handleChartSort("firmName")}>Firma<SortIcon col="firmName" /></th>
                          <th className="text-right sortable-th" onClick={() => handleChartSort("orderCount")}>Sipariş<SortIcon col="orderCount" /></th>
                          <th className="text-right sortable-th" onClick={() => handleChartSort("totalQuantity")}>Miktar<SortIcon col="totalQuantity" /></th>
                          <th className="text-right sortable-th" onClick={() => handleChartSort("totalPieceCount")}>Parça<SortIcon col="totalPieceCount" /></th>
                          <th className="text-right sortable-th" onClick={() => handleChartSort("revenueTRY")}>TRY Ciro<SortIcon col="revenueTRY" /></th>
                          <th className="text-right sortable-th" onClick={() => handleChartSort("revenueUSD")}>USD Ciro<SortIcon col="revenueUSD" /></th>
                          <th className="text-right sortable-th" onClick={() => handleChartSort("revenueEUR")}>EUR Ciro<SortIcon col="revenueEUR" /></th>
                          <th className="text-right sortable-th" onClick={() => handleChartSort("totalRevenueTRY")}>Toplam TRY<SortIcon col="totalRevenueTRY" /></th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedData(filteredCustomerRevenue).map((r) => (
                          <tr key={r.firmId}>
                            <td>{r.firmName || "-"}</td>
                            <td className="text-right">{formatNumber(r.orderCount, 0)}</td>
                            <td className="text-right">{formatNumber(r.totalQuantity)}</td>
                            <td className="text-right">{formatNumber(r.totalPieceCount, 0)}</td>
                            <td className="text-right">{formatNumber(r.revenueTRY)}</td>
                            <td className="text-right">{formatNumber(r.revenueUSD)}</td>
                            <td className="text-right">{formatNumber(r.revenueEUR)}</td>
                            <td className="text-right" style={{ fontWeight: 600 }}>{formatNumber(r.totalRevenueTRY)}</td>
                          </tr>
                        ))}
                        {filteredCustomerRevenue.length === 0 && <tr><td colSpan={8} style={{ textAlign: "center", color: "#999" }}>Veri bulunamadı</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* AYLIK KÂR */}
              {activeTab === "monthly-profit" && (
                <div className="chart-section">
                  <div className="chart-kpi-row">
                    <div className="chart-kpi-card" style={{ borderLeftColor: "#4338ca" }}>
                      <span className="chart-kpi-value">{formatCurrency(monthlyProfit.reduce((s, r) => s + r.totalRevenueTRY, 0), "TRY")}</span>
                      <span className="chart-kpi-label">Toplam Ciro</span>
                    </div>
                    <div className="chart-kpi-card" style={{ borderLeftColor: "#dc2626" }}>
                      <span className="chart-kpi-value">{formatCurrency(monthlyProfit.reduce((s, r) => s + r.totalCostWithOverhead, 0), "TRY")}</span>
                      <span className="chart-kpi-label">Toplam Maliyet</span>
                    </div>
                    <div className="chart-kpi-card" style={{ borderLeftColor: "#0f766e" }}>
                      <span className="chart-kpi-value">{formatCurrency(monthlyProfit.reduce((s, r) => s + r.netProfit, 0), "TRY")}</span>
                      <span className="chart-kpi-label">Net Kâr</span>
                    </div>
                    <div className="chart-kpi-card" style={{ borderLeftColor: "#b45309" }}>
                      <span className="chart-kpi-value">
                        {(() => {
                          const totalRev = monthlyProfit.reduce((s, r) => s + r.totalRevenueTRY, 0);
                          const totalCostOH = monthlyProfit.reduce((s, r) => s + r.totalCostWithOverhead, 0);
                          return totalCostOH > 0 ? `%${(((totalRev - totalCostOH) / totalCostOH) * 100).toFixed(1)}` : "-";
                        })()}
                      </span>
                      <span className="chart-kpi-label">Kâr Marjı</span>
                    </div>
                  </div>
                  <div className="chart-tile">
                    <h3>Aylık Ciro vs Maliyet — {chartYear}</h3>
                    <ResponsiveContainer width="100%" height={400}>
                      <ComposedChart data={monthlyProfit} margin={{ top: 10, right: 30, left: 10, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                        <XAxis dataKey="monthName" tick={{ fontSize: 12 }} />
                        <YAxis yAxisId="left" tick={{ fontSize: 11 }} tickFormatter={(v) => formatNumber(v)} />
                        <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} tickFormatter={(v) => `%${v}`} />
                        <Tooltip formatter={(v: any, name: any) => name === "Kâr Marjı %" ? `%${Number(v).toFixed(1)}` : formatNumber(Number(v))} />
                        <Legend />
                        <Bar yAxisId="left" dataKey="totalRevenueTRY" name="Ciro" fill="#4338ca" radius={[4, 4, 0, 0]} />
                        <Bar yAxisId="left" dataKey="totalCostWithOverhead" name="Maliyet (G.Gider Dahil)" fill="#dc2626" radius={[4, 4, 0, 0]} opacity={0.75} />
                        <Line yAxisId="right" type="monotone" dataKey="profitMarginPercent" name="Kâr Marjı %" stroke="#b45309" strokeWidth={2.5} dot={{ r: 4 }} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="chart-tile">
                    <h3>Aylık Net Kâr Trendi — {chartYear}</h3>
                    <ResponsiveContainer width="100%" height={350}>
                      <AreaChart data={monthlyProfit} margin={{ top: 10, right: 30, left: 10, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                        <XAxis dataKey="monthName" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => formatNumber(v)} />
                        <Tooltip formatter={(v: any) => formatNumber(Number(v))} />
                        <Legend />
                        <Area type="monotone" dataKey="grossProfit" name="Brüt Kâr" stroke="#6366f1" fill="#6366f1" fillOpacity={0.08} strokeWidth={2} />
                        <Area type="monotone" dataKey="netProfit" name="Net Kâr" stroke="#0f766e" fill="#0f766e" fillOpacity={0.12} strokeWidth={2.5} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="chart-tile">
                    <h3>Detay Tablosu</h3>
                    <table className="chart-detail-table">
                      <thead>
                        <tr>
                          <th className="sortable-th" onClick={() => handleChartSort("monthName")}>Ay<SortIcon col="monthName" /></th>
                          <th className="text-right sortable-th" onClick={() => handleChartSort("orderCount")}>Sipariş<SortIcon col="orderCount" /></th>
                          <th className="text-right sortable-th" onClick={() => handleChartSort("totalRevenueTRY")}>Ciro TRY<SortIcon col="totalRevenueTRY" /></th>
                          <th className="text-right sortable-th" onClick={() => handleChartSort("totalCostTRY")}>Maliyet TRY<SortIcon col="totalCostTRY" /></th>
                          <th className="text-right sortable-th" onClick={() => handleChartSort("totalCostWithOverhead")}>G.Gider Dahil<SortIcon col="totalCostWithOverhead" /></th>
                          <th className="text-right sortable-th" onClick={() => handleChartSort("grossProfit")}>Brüt Kâr<SortIcon col="grossProfit" /></th>
                          <th className="text-right sortable-th" onClick={() => handleChartSort("netProfit")}>Net Kâr<SortIcon col="netProfit" /></th>
                          <th className="text-right sortable-th" onClick={() => handleChartSort("profitMarginPercent")}>Kâr Marjı<SortIcon col="profitMarginPercent" /></th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedData(monthlyProfit).map((r) => (
                          <tr key={`${r.year}-${r.month}`}>
                            <td>{r.monthName}</td>
                            <td className="text-right">{formatNumber(r.orderCount, 0)}</td>
                            <td className="text-right">{formatNumber(r.totalRevenueTRY)}</td>
                            <td className="text-right">{formatNumber(r.totalCostTRY)}</td>
                            <td className="text-right">{formatNumber(r.totalCostWithOverhead)}</td>
                            <td className="text-right" style={{ color: r.grossProfit >= 0 ? "#059669" : "#ef4444" }}>{formatNumber(r.grossProfit)}</td>
                            <td className="text-right" style={{ color: r.netProfit >= 0 ? "#059669" : "#ef4444", fontWeight: 600 }}>{formatNumber(r.netProfit)}</td>
                            <td className="text-right" style={{ color: r.profitMarginPercent >= 0 ? "#059669" : "#ef4444" }}>%{r.profitMarginPercent.toFixed(1)}</td>
                          </tr>
                        ))}
                        {monthlyProfit.length === 0 && <tr><td colSpan={8} style={{ textAlign: "center", color: "#999" }}>Veri bulunamadı</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Existing Table Report - only show when table tab active */}
      {activeTab === "table" && (
      <>
      {/* Filtreler */}
      <div className="reports-filters">
        <div className="reports-filters-row">
        <div className="filter-group">
          <label>Firma</label>
          <div className="searchable-select">
            <div
              className="select-header"
              onClick={() => setFirmDropdownOpen(!firmDropdownOpen)}
            >
              <span>
                {selectedFirmId === "all"
                  ? "Tüm Firmalar"
                  : firms.find((f) => f.firmId === selectedFirmId)?.firmName ||
                    "Seçiniz"}
              </span>
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M7 10l5 5 5-5z" />
              </svg>
            </div>
            {firmDropdownOpen && (
              <div className="select-dropdown">
                <input
                  type="text"
                  placeholder="Firma ara..."
                  value={firmSearch}
                  onChange={(e) => setFirmSearch(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  autoFocus
                />
                <div className="select-options">
                  <div
                    className={`select-option ${
                      selectedFirmId === "all" ? "selected" : ""
                    }`}
                    onClick={() => {
                      setSelectedFirmId("all");
                      setFirmDropdownOpen(false);
                      setFirmSearch("");
                    }}
                  >
                    Tüm Firmalar
                  </div>
                  {firms
                    .filter(
                      (f) =>
                        f.firmCode
                          .toLowerCase()
                          .includes(firmSearch.toLowerCase()) ||
                        f.firmName
                          .toLowerCase()
                          .includes(firmSearch.toLowerCase()),
                    )
                    .sort((a, b) => a.firmName.localeCompare(b.firmName))
                    .map((firm) => (
                      <div
                        key={firm.firmId}
                        className={`select-option ${
                          selectedFirmId === firm.firmId ? "selected" : ""
                        }`}
                        onClick={() => {
                          setSelectedFirmId(firm.firmId);
                          setFirmDropdownOpen(false);
                          setFirmSearch("");
                        }}
                      >
                        {firm.firmName}
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="filter-group">
          <label>Model</label>
          <div className="searchable-select">
            <div
              className="select-header"
              onClick={() => setModelDropdownOpen(!modelDropdownOpen)}
            >
              <span>
                {selectedModelId === "all"
                  ? "Tüm Modeller"
                  : models.find((m) => m.modelId === selectedModelId)
                      ?.modelCode || "Seçiniz"}
              </span>
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M7 10l5 5 5-5z" />
              </svg>
            </div>
            {modelDropdownOpen && (
              <div className="select-dropdown">
                <input
                  type="text"
                  placeholder="Model ara..."
                  value={modelSearch}
                  onChange={(e) => setModelSearch(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  autoFocus
                />
                <div className="select-options">
                  <div
                    className={`select-option ${
                      selectedModelId === "all" ? "selected" : ""
                    }`}
                    onClick={() => {
                      setSelectedModelId("all");
                      setModelDropdownOpen(false);
                      setModelSearch("");
                    }}
                  >
                    Tüm Modeller
                  </div>
                  {models
                    .filter((m) =>
                      m.modelCode
                        .toLowerCase()
                        .includes(modelSearch.toLowerCase()),
                    )
                    .sort((a, b) => a.modelCode.localeCompare(b.modelCode))
                    .map((model) => (
                      <div
                        key={model.modelId}
                        className={`select-option ${
                          selectedModelId === model.modelId ? "selected" : ""
                        }`}
                        onClick={() => {
                          setSelectedModelId(model.modelId);
                          setModelDropdownOpen(false);
                          setModelSearch("");
                        }}
                      >
                        {model.modelCode}
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="filter-group">
          <label>Başlangıç Tarihi</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="date-input"
          />
        </div>
        <div className="filter-group">
          <label>Bitiş Tarihi</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="date-input"
          />
        </div>

        {/* Boya Adedi Filtresi */}
        <div className="filter-group paint-count-filter-group">
          <label>Boya Adedi</label>
          <div
            className={`range-filter ${
              paintCountMin || paintCountMax ? "is-active" : ""
            }`}
          >
            <input
              type="number"
              min="0"
              step="1"
              inputMode="numeric"
              className="range-filter-input"
              placeholder="Min"
              value={paintCountMin}
              onChange={(e) => setPaintCountMin(e.target.value)}
              title="En az bu kadar boya çeşidi olan siparişler"
            />
            <span className="range-filter-sep">–</span>
            <input
              type="number"
              min="0"
              step="1"
              inputMode="numeric"
              className="range-filter-input"
              placeholder="Max"
              value={paintCountMax}
              onChange={(e) => setPaintCountMax(e.target.value)}
              title="En fazla bu kadar boya çeşidi olan siparişler"
            />
            {(paintCountMin || paintCountMax) && (
              <button
                type="button"
                className="range-filter-clear"
                onClick={() => {
                  setPaintCountMin("");
                  setPaintCountMax("");
                }}
                title="Boya adedi filtresini temizle"
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Teknik Filtresi - detayları alt satırdaki technic-filter-bar'da */}
        <div className="filter-group technic-filter-group">
          <label>Teknikler</label>
          <button
            type="button"
            className={`technic-filter-trigger ${
              selectedTechnics.length > 0 ? "is-active" : ""
            }`}
            onClick={() => setShowTechnicModal(true)}
          >
            <span className="technic-filter-trigger-icon">
              <svg
                viewBox="0 0 24 24"
                width="16"
                height="16"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polygon points="12 2 2 7 12 12 22 7 12 2" />
                <polyline points="2 17 12 22 22 17" />
                <polyline points="2 12 12 17 22 12" />
              </svg>
            </span>
            <span className="technic-filter-trigger-text">
              {selectedTechnics.length === 0
                ? "Teknik Seç"
                : `${selectedTechnics.length} teknik seçili`}
            </span>
            <svg
              className="technic-filter-caret"
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M7 10l5 5 5-5z" />
            </svg>
          </button>
        </div>

        {/* Kar Durumu Filtresi */}
        <div className="filter-group">
          <label>Kar Durumu</label>
          <select
            value={profitStatusFilter}
            onChange={(e) => setProfitStatusFilter(e.target.value)}
            className="profit-status-select"
          >
            <option value="all">Tümü</option>
            <option value="above">
              Hedef Üstü (≥%{settings?.profitMargin || 40})
            </option>
            <option value="below">
              Hedef Altı (&lt;%{settings?.profitMargin})
            </option>
            <option value="loss">Zarar</option>
          </select>
        </div>

        {(startDate ||
          endDate ||
          profitStatusFilter !== "all" ||
          paintCountMin ||
          paintCountMax ||
          selectedTechnics.length > 0) && (
          <button
            className="btn btn-sm btn-secondary"
            onClick={() => {
              setStartDate("");
              setEndDate("");
              setProfitStatusFilter("all");
              setPaintCountMin("");
              setPaintCountMax("");
              setSelectedTechnics([]);
              setTechnicMatchMode("exact");
            }}
          >
            Filtreleri Temizle
          </button>
        )}
        <div className="filter-summary">
          <span className="summary-item">
            <strong>{filteredData.length}</strong> Kayıt
          </span>
          <span className="summary-item">
            <strong>{formatNumber(totalQuantity)}</strong> Adet
          </span>
        </div>
        <div
          style={{
            display: "flex",
            gap: "10px",
            alignItems: "center",
            justifyContent: "flex-end",
            marginLeft: "auto",
          }}
        >
          <button
            className="btn btn-success"
            onClick={loadAllOrders}
            disabled={isLoadingAll || !hasMore}
            style={{
              background: hasMore
                ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                : "#cbd5e1",
              opacity: isLoadingAll || !hasMore ? 0.6 : 1,
              cursor: isLoadingAll || !hasMore ? "not-allowed" : "pointer",
              color: hasMore ? "#fff" : "#64748b",
            }}
          >
            {isLoadingAll ? (
              <>
                <div
                  style={{
                    width: "16px",
                    height: "16px",
                    border: "2px solid #fff",
                    borderTop: "2px solid transparent",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                  }}
                ></div>
                Yükleniyor...
              </>
            ) : (
              <>
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                  <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
                  <line x1="12" y1="22.08" x2="12" y2="12" />
                </svg>
                Tüm Siparişleri Yükle
              </>
            )}
          </button>
          <button
            className="btn btn-success"
            onClick={handleExportExcel}
            disabled={filteredData.length === 0}
            style={{
              opacity: filteredData.length === 0 ? 0.5 : 1,
              cursor: filteredData.length === 0 ? "not-allowed" : "pointer",
            }}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="7 10 12 15 17 10"></polyline>
              <line x1="12" y1="15" x2="12" y2="3"></line>
            </svg>
            Excel İndir
          </button>
        </div>
        </div>

        {/* Teknik filtresi detay barı - sadece teknik seçiliyken */}
        {selectedTechnics.length > 0 && (
          <div className="technic-filter-bar">
            <div className="technic-filter-bar-modes">
              <span className="technic-filter-bar-label">Eşleşme</span>
              <div
                className="technic-match-modes"
                role="radiogroup"
                aria-label="Teknik eşleşme modu"
              >
                {TECHNIC_MATCH_MODES.map((mode) => (
                  <button
                    key={mode.value}
                    type="button"
                    role="radio"
                    aria-checked={technicMatchMode === mode.value}
                    className={`technic-match-mode ${
                      technicMatchMode === mode.value ? "is-active" : ""
                    }`}
                    onClick={() => setTechnicMatchMode(mode.value)}
                    title={mode.hint}
                  >
                    {mode.label}
                  </button>
                ))}
              </div>
              <span className="technic-match-hint">
                {getTechnicMatchHint(technicMatchMode)}
              </span>
            </div>

            <div className="technic-filter-chips">
              {selectedTechnics.map((tech) => (
                <span key={tech.technicId} className="technic-filter-chip">
                  {tech.name}
                  <button
                    type="button"
                    onClick={() => handleTechnicSelect(tech)}
                    aria-label={`${tech.name} filtreden kaldır`}
                  >
                    <svg
                      viewBox="0 0 24 24"
                      width="11"
                      height="11"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                    >
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </span>
              ))}
              <button
                type="button"
                className="technic-filter-chips-clear"
                onClick={handleClearTechnics}
              >
                Tümünü temizle
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Özet Kartları */}
      <div className="reports-summary-cards">
        <div className="summary-card">
          <div className="card-icon blue">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
              <line x1="1" y1="10" x2="23" y2="10" />
            </svg>
          </div>
          <div className="card-content">
            <span className="card-label">Toplam Tutar</span>
            <span className="card-value">
              {formatCurrency(totalAmount, "TRY")}
            </span>
          </div>
        </div>
        <div className="summary-card">
          <div className="card-icon orange">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M12 6v6l4 2" />
            </svg>
          </div>
          <div className="card-content">
            <span className="card-label">
              Toplam Maliyet (Genel Gider Dahil)
            </span>
            <span className="card-value">
              {formatCurrency(totalCostWithOverhead, "TRY")}
            </span>
          </div>
        </div>
        <div className="summary-card">
          <div
            className="card-icon purple"
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
              <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
              <line x1="12" y1="22.08" x2="12" y2="12" />
            </svg>
          </div>
          <div className="card-content">
            <span className="card-label">Hesaplanan Tutar (Kar Dahil)</span>
            <span className="card-value" style={{ color: "#667eea" }}>
              {formatCurrency(totalFinalCost, "TRY")}
            </span>
          </div>
        </div>
        <div className="summary-card">
          <div className="card-icon green">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
              <polyline points="17 6 23 6 23 12" />
            </svg>
          </div>
          <div className="card-content">
            <span className="card-label">Kar/Zarar</span>
            <span
              className={`card-value ${
                totalAmount - totalCostWithOverhead >= 0
                  ? "positive"
                  : "negative"
              }`}
            >
              {formatCurrency(totalAmount - totalCostWithOverhead, "TRY")}
            </span>
          </div>
        </div>
        <div className="summary-card">
          <div
            className={`card-icon ${
              totalAmount > 0 &&
              ((totalAmount - totalCostWithOverhead) / totalAmount) * 100 >= 0
                ? "green"
                : "red"
            }`}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M12 8v8" />
              <path d="M8 12h8" />
            </svg>
          </div>
          <div className="card-content">
            <span className="card-label">Kar/Zarar Oranı</span>
            <span
              className={`card-value ${
                totalAmount > 0 &&
                ((totalAmount - totalCostWithOverhead) / totalAmount) * 100 >= 0
                  ? "positive"
                  : "negative"
              }`}
            >
              {totalAmount > 0
                ? `%${(
                    ((totalAmount - totalCostWithOverhead) /
                      totalCostWithOverhead) *
                    100
                  ).toFixed(2)}`
                : "%0.00"}
            </span>
          </div>
        </div>
      </div>

      {/* Tablo - Desktop */}
      <div className="reports-table-container desktop-only">
        <table className="reports-table">
          <thead>
            <tr>
              <th style={{ width: "60px" }}>Resim</th>
              <th>Sipariş No</th>
              <th onClick={() => handleSort("firmName")} className="sortable">
                Firma {getSortIcon("firmName")}
              </th>
              <th onClick={() => handleSort("modelCode")} className="sortable">
                Model Kodu {getSortIcon("modelCode")}
              </th>
              <th onClick={() => handleSort("quantity")} className="sortable">
                Adet {getSortIcon("quantity")}
              </th>
              <th>Teknikler</th>
              <th
                onClick={() => handleSort("paintCount")}
                className="sortable text-center"
              >
                Boya Adedi {getSortIcon("paintCount")}
              </th>
              <th
                onClick={() => handleSort("totalAmount")}
                className="sortable text-right"
              >
                Toplam Tutar {getSortIcon("totalAmount")}
              </th>
              <th
                onClick={() => handleSort("totalCost")}
                className="sortable text-right"
              >
                Maliyet {getSortIcon("totalCost")}
              </th>
              <th className="text-right" style={{ color: "#667eea" }}>
                Genel Gider (+%40)
              </th>
              <th className="text-right" style={{ color: "#667eea" }}>
                Hesaplanan Tutar
              </th>
              <th
                onClick={() => handleSort("unitPrice")}
                className="sortable text-right"
              >
                Birim Fiyat {getSortIcon("unitPrice")}
              </th>
              <th
                onClick={() => handleSort("unitCost")}
                className="sortable text-right"
              >
                Birim Maliyet {getSortIcon("unitCost")}
              </th>
              <th
                onClick={() => handleSort("suggestedPrice")}
                className="sortable text-right"
              >
                Hesaplanan Fiyat {getSortIcon("suggestedPrice")}
              </th>
              <th
                onClick={() => handleSort("profit")}
                className="sortable text-right"
              >
                Kar/Zarar {getSortIcon("profit")}
              </th>
              <th className="text-center">Detay</th>
            </tr>
          </thead>
          <tbody>
            {filteredData.length === 0 ? (
              <tr>
                <td colSpan={16} className="empty-row">
                  Gösterilecek veri bulunamadı
                </td>
              </tr>
            ) : (
              filteredData.map((row, index) => (
                <tr
                  key={`${row.orderId}-${index}`}
                  className={`profit-status-${getProfitStatus(row)}`}
                >
                  <td className="text-center report-thumbnail-cell">
                    {orderThumbnails[row.orderId] ? (
                      <img
                        src={getImageUrl(orderThumbnails[row.orderId]!)}
                        alt="Sipariş"
                        className="report-thumbnail"
                        onClick={() => {
                          setSelectedOrderForImages(row.orderId);
                          setShowImageManager(true);
                        }}
                      />
                    ) : orderThumbnails[row.orderId] === null ? (
                      <span
                        className="report-no-image"
                        title="Resim yok - Eklemek için tıklayın"
                        onClick={() => {
                          setSelectedOrderForImages(row.orderId);
                          setShowImageManager(true);
                        }}
                      >
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#cbd5e1"
                          strokeWidth="1.5"
                        >
                          <rect
                            x="3"
                            y="3"
                            width="18"
                            height="18"
                            rx="2"
                            ry="2"
                          ></rect>
                          <circle cx="8.5" cy="8.5" r="1.5"></circle>
                          <polyline points="21 15 16 10 5 21"></polyline>
                        </svg>
                      </span>
                    ) : (
                      <span className="report-thumbnail-loading">...</span>
                    )}
                  </td>
                  <td
                    style={{
                      fontFamily: "monospace",
                      fontSize: "11px",
                      color: "#666",
                      cursor: "pointer",
                    }}
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(row.orderId);
                        setAlertModal({
                          isOpen: true,
                          message: `Order ID kopyalandı: ${row.orderId}`,
                          type: "success",
                        });
                      } catch (err) {
                        // Fallback: Manuel seçim yöntemi
                        const textArea = document.createElement("textarea");
                        textArea.value = row.orderId;
                        textArea.style.position = "fixed";
                        textArea.style.opacity = "0";
                        document.body.appendChild(textArea);
                        textArea.select();
                        try {
                          document.execCommand("copy");
                          setAlertModal({
                            isOpen: true,
                            message: `Order ID kopyalandı: ${row.orderId}`,
                            type: "success",
                          });
                        } catch (e) {
                          setAlertModal({
                            isOpen: true,
                            message: "Kopyalama başarısız oldu.",
                            type: "error",
                          });
                        } finally {
                          document.body.removeChild(textArea);
                        }
                      }
                    }}
                    title="Kopyalamak için tıklayın"
                  >
                    {row.orderId.substring(0, 8)}...
                  </td>
                  <td>
                    <span className="firm-code">{row.firmName}</span>
                  </td>
                  <td>
                    <span className="model-code">{row.modelCode}</span>
                  </td>
                  <td className="text-center">
                    <span className="quantity-badge">
                      {formatNumber(row.quantity)}
                    </span>
                  </td>
                  <td>
                    {row.technics.length > 0 ? (
                      row.technics.length <= 3 ? (
                        <div className="technics-list">
                          {row.technics.map((t, i) => (
                            <span key={i} className="technic-tag">
                              {t}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <div className="technics-expandable">
                          {expandedTechnics.has(row.orderId) ? (
                            <>
                              <div className="technics-list">
                                {row.technics.map((t, i) => (
                                  <span key={i} className="technic-tag">
                                    {t}
                                  </span>
                                ))}
                              </div>
                              <button
                                className="technics-toggle"
                                onClick={() => {
                                  const newSet = new Set(expandedTechnics);
                                  newSet.delete(row.orderId);
                                  setExpandedTechnics(newSet);
                                }}
                              >
                                Gizle ▲
                              </button>
                            </>
                          ) : (
                            <>
                              <div className="technics-list">
                                {row.technics.slice(0, 2).map((t, i) => (
                                  <span key={i} className="technic-tag">
                                    {t}
                                  </span>
                                ))}
                              </div>
                              <button
                                className="technics-toggle"
                                onClick={() => {
                                  const newSet = new Set(expandedTechnics);
                                  newSet.add(row.orderId);
                                  setExpandedTechnics(newSet);
                                }}
                              >
                                +{row.technics.length - 2} daha ▼
                              </button>
                            </>
                          )}
                        </div>
                      )
                    ) : (
                      <span className="no-technic">-</span>
                    )}
                  </td>
                  <td className="text-center">
                    {row.paintCount > 0 ? (
                      <span className="paint-count-badge">
                        {Number(row.paintCount)}
                      </span>
                    ) : (
                      <span className="no-technic">-</span>
                    )}
                  </td>
                  <td className="text-right">
                    <span className="amount">
                      {formatCurrency(row.totalAmount, "TRY")}
                    </span>
                  </td>
                  <td className="text-right">
                    <span className="cost">
                      {formatCurrency(row.totalCost, "TRY")}
                    </span>
                  </td>
                  <td className="text-right">
                    <span
                      className="cost"
                      style={{ color: "#667eea", fontWeight: "600" }}
                    >
                      {formatCurrency(row.costWithOverhead, "TRY")}
                    </span>
                  </td>
                  <td className="text-right">
                    <span
                      className="cost"
                      style={{ color: "#667eea", fontWeight: "600" }}
                    >
                      {formatCurrency(row.finalCostWithProfit, "TRY")}
                    </span>
                  </td>
                  <td className="text-right">
                    <div className="price-with-currency">
                      <span className="unit-price">
                        {formatCurrency(row.unitPrice, row.currency)}
                      </span>
                      {row.currency !== "TRY" && row.currency !== "TL" && (
                        <span className="price-try">
                          ≈ {formatCurrency(row.unitPriceTRY, "TRY")}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="text-right">
                    <span className="unit-cost">
                      {formatCurrency(row.unitCost, "TRY")}
                    </span>
                  </td>
                  <td className="text-right">
                    <span className="suggested-price">
                      {formatCurrency(row.suggestedPrice, "TRY")}
                    </span>
                  </td>
                  <td className="text-right">
                    <div className="profit-loss-cell">
                      <div
                        className={`profit-amount-wrapper ${
                          row.totalAmount - row.costWithOverhead >= 0
                            ? "profit-positive"
                            : "profit-negative"
                        }`}
                      >
                        <span className="profit-amount">
                          {formatCurrency(
                            row.totalAmount - row.costWithOverhead,
                            "TRY",
                          )}
                        </span>
                      </div>
                      <div className="profit-rate-wrapper">
                        <span
                          className={`profit-rate-badge profit-rate-badge-${getProfitStatus(row)}`}
                        >
                          <span className="profit-rate-label">Oran:</span>
                          <span className="profit-rate-value">
                            %{getProfitRate(row).toFixed(1)}
                          </span>
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="text-center">
                    <div
                      style={{
                        display: "flex",
                        gap: "8px",
                        justifyContent: "center",
                      }}
                    >
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => openCostDetail(row)}
                        title="Maliyet Detayları"
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                        Detay
                      </button>
                      {row.modelId && (
                        <button
                          onClick={() =>
                            setOrderCostsModal({
                              isOpen: true,
                              orderId: row.orderId,
                              modelId: row.modelId,
                            })
                          }
                          className="btn btn-sm btn-soft"
                          title="Sipariş Maliyetleri"
                          style={{
                            padding: "8px 12px",
                            background: "#10b981",
                            color: "white",
                            border: "none",
                            borderRadius: "6px",
                            cursor: "pointer",
                            fontSize: "13px",
                            fontWeight: "500",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            transition: "all 0.2s ease",
                          }}
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <line x1="12" y1="1" x2="12" y2="23"></line>
                            <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                          </svg>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
          {filteredData.length > 0 && (
            <tfoot>
              <tr>
                <td colSpan={4}>
                  <strong>TOPLAM</strong>
                </td>
                <td className="text-center">
                  <strong>{formatNumber(totalQuantity)}</strong>
                </td>
                <td></td>
                <td className="text-right">
                  <strong>{formatCurrency(totalAmount, "TRY")}</strong>
                </td>
                <td className="text-right">
                  <strong>{formatCurrency(totalCost, "TRY")}</strong>
                </td>
                <td className="text-right">
                  <strong>
                    {formatCurrency(totalCostWithOverhead, "TRY")}
                  </strong>
                </td>
                <td className="text-right">
                  <strong>{formatCurrency(totalFinalCost, "TRY")}</strong>
                </td>
                <td></td>
                <td></td>
                <td></td>
                <td className="text-right">
                  <strong
                    className={
                      totalAmount - totalCostWithOverhead >= 0
                        ? "positive"
                        : "negative"
                    }
                  >
                    {formatCurrency(totalAmount - totalCostWithOverhead, "TRY")}
                  </strong>
                </td>
                <td></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* Mobil Kart Görünümü */}
      <div className="reports-cards-container mobile-only">
        {filteredData.length === 0 ? (
          <div className="empty-state">
            <p>Gösterilecek veri bulunamadı</p>
          </div>
        ) : (
          filteredData.map((row, index) => (
            <div
              key={`${row.orderId}-${index}`}
              className={`report-card profit-status-${getProfitStatus(row)}`}
            >
              <div className="report-card-header">
                {orderThumbnails[row.orderId] ? (
                  <div className="card-thumbnail-wrapper">
                    <img
                      src={getImageUrl(orderThumbnails[row.orderId]!)}
                      alt="Sipariş"
                      className="card-thumbnail"
                      onClick={() => {
                        setSelectedOrderForImages(row.orderId);
                        setShowImageManager(true);
                      }}
                    />
                  </div>
                ) : orderThumbnails[row.orderId] === null ? (
                  <div
                    className="card-thumbnail-wrapper card-no-image"
                    onClick={() => {
                      setSelectedOrderForImages(row.orderId);
                      setShowImageManager(true);
                    }}
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#cbd5e1"
                      strokeWidth="1.5"
                    >
                      <rect
                        x="3"
                        y="3"
                        width="18"
                        height="18"
                        rx="2"
                        ry="2"
                      ></rect>
                      <circle cx="8.5" cy="8.5" r="1.5"></circle>
                      <polyline points="21 15 16 10 5 21"></polyline>
                    </svg>
                  </div>
                ) : null}
                <div className="card-header-info">
                  <div className="card-firm">
                    <span className="firm-code">{row.firmName}</span>
                  </div>
                  <div className="card-model">
                    <span className="model-code">{row.modelCode}</span>
                  </div>
                </div>
              </div>

              <div className="report-card-body">
                <div className="card-row">
                  <span className="card-label">Sipariş No:</span>
                  <span
                    style={{
                      fontFamily: "monospace",
                      fontSize: "11px",
                      color: "#666",
                      cursor: "pointer",
                    }}
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(row.orderId);
                        setAlertModal({
                          isOpen: true,
                          message: `Order ID kopyalandı: ${row.orderId}`,
                          type: "success",
                        });
                      } catch (err) {
                        const textArea = document.createElement("textarea");
                        textArea.value = row.orderId;
                        textArea.style.position = "fixed";
                        textArea.style.opacity = "0";
                        document.body.appendChild(textArea);
                        textArea.select();
                        try {
                          document.execCommand("copy");
                          setAlertModal({
                            isOpen: true,
                            message: `Order ID kopyalandı: ${row.orderId}`,
                            type: "success",
                          });
                        } catch (e) {
                          setAlertModal({
                            isOpen: true,
                            message: "Kopyalama başarısız oldu.",
                            type: "error",
                          });
                        } finally {
                          document.body.removeChild(textArea);
                        }
                      }
                    }}
                    title="Kopyalamak için tıklayın"
                  >
                    {row.orderId.substring(0, 8)}...
                  </span>
                </div>

                <div className="card-row">
                  <span className="card-label">Adet:</span>
                  <span className="quantity-badge">
                    {formatNumber(row.quantity)}
                  </span>
                </div>

                {row.technics.length > 0 && (
                  <div className="card-row technics-row">
                    <span className="card-label">Teknikler:</span>
                    <div className="technics-list">
                      {row.technics.slice(0, 3).map((t, i) => (
                        <span key={i} className="technic-tag">
                          {t}
                        </span>
                      ))}
                      {row.technics.length > 3 && (
                        <span className="technic-tag more">
                          +{row.technics.length - 3}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {row.paintCount > 0 && (
                  <div className="card-row">
                    <span className="card-label">Boya Adedi:</span>
                    <span className="paint-count-badge">
                      {Number(row.paintCount)}
                    </span>
                  </div>
                )}

                <div className="card-row">
                  <span className="card-label">Toplam Tutar:</span>
                  <span className="amount">
                    {formatCurrency(row.totalAmount, "TRY")}
                  </span>
                </div>

                <div className="card-row">
                  <span className="card-label">Maliyet:</span>
                  <span className="cost">
                    {formatCurrency(row.totalCost, "TRY")}
                  </span>
                </div>

                <div className="card-row">
                  <span className="card-label">Genel Gider Dahil:</span>
                  <span className="cost" style={{ color: "#667eea" }}>
                    {formatCurrency(row.costWithOverhead, "TRY")}
                  </span>
                </div>

                <div className="card-row">
                  <span className="card-label">Birim Fiyat:</span>
                  <div className="price-with-currency">
                    <span className="unit-price">
                      {formatCurrency(row.unitPrice, row.currency)}
                    </span>
                    {row.currency !== "TRY" && row.currency !== "TL" && (
                      <span className="price-try">
                        ≈ {formatCurrency(row.unitPriceTRY, "TRY")}
                      </span>
                    )}
                  </div>
                </div>

                <div className="card-row">
                  <span className="card-label">Birim Maliyet:</span>
                  <span className="unit-cost">
                    {formatCurrency(row.unitCost, "TRY")}
                  </span>
                </div>

                <div className="card-row">
                  <span className="card-label">Hesaplanan Fiyat:</span>
                  <span className="suggested-price">
                    {formatCurrency(row.suggestedPrice, "TRY")}
                  </span>
                </div>

                <div className="card-row profit-row">
                  <span className="card-label">Kar/Zarar:</span>
                  <div className="profit-loss-cell-mobile">
                    <span
                      className={`profit-loss ${
                        row.totalAmount - row.costWithOverhead >= 0
                          ? "positive"
                          : "negative"
                      }`}
                    >
                      {formatCurrency(
                        row.totalAmount - row.costWithOverhead,
                        "TRY",
                      )}
                    </span>
                    <span
                      className={`profit-rate profit-rate-${getProfitStatus(row)}`}
                    >
                      %{getProfitRate(row).toFixed(1)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="report-card-footer">
                <div style={{ display: "flex", gap: "8px", width: "100%" }}>
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => openCostDetail(row)}
                    style={{ flex: 1 }}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                    Detayları Görüntüle
                  </button>
                  {row.modelId && (
                    <button
                      onClick={() =>
                        setOrderCostsModal({
                          isOpen: true,
                          orderId: row.orderId,
                          modelId: row.modelId,
                        })
                      }
                      className="btn btn-sm btn-soft"
                      title="Sipariş Maliyetleri"
                      style={{
                        padding: "10px 16px",
                        background: "#10b981",
                        color: "white",
                        border: "none",
                        borderRadius: "8px",
                        cursor: "pointer",
                        fontSize: "14px",
                        fontWeight: "500",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "6px",
                        transition: "all 0.2s ease",
                      }}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <line x1="12" y1="1" x2="12" y2="23"></line>
                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}

        {/* Mobil Özet Kartı */}
        {filteredData.length > 0 && (
          <div className="mobile-summary-card">
            <h3>Genel Toplam</h3>
            <div className="summary-row">
              <span className="summary-label">Toplam Adet:</span>
              <span className="summary-value">
                {formatNumber(totalQuantity)}
              </span>
            </div>
            <div className="summary-row">
              <span className="summary-label">Toplam Tutar:</span>
              <span className="summary-value amount">
                {formatCurrency(totalAmount, "TRY")}
              </span>
            </div>
            <div className="summary-row">
              <span className="summary-label">Toplam Maliyet:</span>
              <span className="summary-value cost">
                {formatCurrency(totalCost, "TRY")}
              </span>
            </div>
            <div className="summary-row">
              <span className="summary-label">Genel Gider Dahil:</span>
              <span className="summary-value" style={{ color: "#667eea" }}>
                {formatCurrency(totalCostWithOverhead, "TRY")}
              </span>
            </div>
            <div className="summary-row total">
              <span className="summary-label">Kar/Zarar:</span>
              <span
                className={`summary-value profit-loss ${
                  totalAmount - totalCostWithOverhead >= 0
                    ? "positive"
                    : "negative"
                }`}
              >
                {formatCurrency(totalAmount - totalCostWithOverhead, "TRY")}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Maliyet Detay Modalı */}
      {costDetailModal.isOpen && (
        <div className="cost-detail-modal-overlay">
          <div className="cost-detail-modal">
            <div className="cost-detail-header">
              <div className="cost-detail-title">
                <h2>Maliyet Detayları</h2>
                <div className="cost-detail-info">
                  <span className="info-badge firm">
                    {costDetailModal.firmName}
                  </span>
                  <span className="info-badge model">
                    Model: {costDetailModal.modelCode}
                  </span>
                  <span className="info-badge quantity">
                    Adet: {costDetailModal.quantity}
                  </span>
                </div>
              </div>
              <button className="cost-detail-close" onClick={closeCostDetail}>
                ×
              </button>
            </div>

            <div className="cost-detail-body">
              {costDetailModal.loading ? (
                <div className="cost-detail-loading">
                  <div className="spinner"></div>
                  <p>Maliyet kalemleri yükleniyor...</p>
                </div>
              ) : costDetailModal.costs.length === 0 ? (
                <div className="cost-detail-empty">
                  <svg
                    width="48"
                    height="48"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#ccc"
                    strokeWidth="2"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <path d="M12 16v-4M12 8h.01" />
                  </svg>
                  <p>Bu sipariş için maliyet kalemi bulunamadı.</p>
                </div>
              ) : (
                <>
                  {/* Atölye bazında grupla */}
                  {(() => {
                    // Maliyetleri atölye bazında grupla
                    const groupedByWorkshop = costDetailModal.costs.reduce(
                      (acc, cost) => {
                        const workshopName =
                          cost.order?.workshop?.name || cost.usage || "Genel";
                        if (!acc[workshopName]) {
                          acc[workshopName] = [];
                        }
                        acc[workshopName].push(cost);
                        return acc;
                      },
                      {} as Record<string, typeof costDetailModal.costs>,
                    );
                    const workshopNames = Object.keys(groupedByWorkshop);
                    const grandTotal = costDetailModal.costs.reduce(
                      (sum, c) => sum + convertCostToTRY(c),
                      0,
                    );

                    // Backend'den gelen costWithOverhead ve finalCostWithProfit değerlerini topla
                    const grandTotalWithOverhead = costDetailModal.costs.reduce(
                      (sum, c) => {
                        if (c.costWithOverhead) {
                          // costWithOverhead'i TL'ye çevir
                          return sum + convertCostWithOverheadToTRY(c);
                        }
                        // Eğer costWithOverhead yoksa, totalCost'u TL'ye çevir
                        return sum + convertCostToTRY(c);
                      },
                      0,
                    );

                    const grandTotalWithProfit = costDetailModal.costs.reduce(
                      (sum, c) => {
                        if (c.finalCostWithProfit) {
                          // finalCostWithProfit'i TL'ye çevir
                          return sum + convertFinalCostToTRY(c);
                        }
                        // Eğer finalCostWithProfit yoksa, totalCost'u TL'ye çevir
                        return sum + convertCostToTRY(c);
                      },
                      0,
                    );

                    return (
                      <div className="cost-detail-groups">
                        {workshopNames.map((workshopName) => {
                          const costs = groupedByWorkshop[workshopName];
                          const workshopTotal = costs.reduce(
                            (sum, c) => sum + convertCostToTRY(c),
                            0,
                          );

                          return (
                            <div
                              key={workshopName}
                              className="cost-detail-group"
                            >
                              <div className="cost-group-header">
                                <div className="cost-group-title">
                                  <svg
                                    width="18"
                                    height="18"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                  >
                                    <rect
                                      x="2"
                                      y="7"
                                      width="20"
                                      height="14"
                                      rx="2"
                                      ry="2"
                                    />
                                    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                                  </svg>
                                  <span>{workshopName}</span>
                                </div>
                                <div className="cost-group-total">
                                  {formatCurrency(workshopTotal, "TRY")}
                                </div>
                              </div>

                              <div className="cost-detail-table-wrapper">
                                <table className="cost-detail-table">
                                  <thead>
                                    <tr>
                                      <th>Maliyet Kalemi</th>
                                      <th>Kategori</th>
                                      <th className="text-center">Miktar</th>
                                      <th>Birim</th>
                                      <th className="text-center">Fire %</th>
                                      <th className="text-right">
                                        Birim Fiyat
                                      </th>
                                      <th className="text-right">Toplam</th>
                                      <th className="text-right">
                                        TL Karşılığı
                                      </th>
                                      <th>Detay/Not</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {costs.map((cost) => {
                                      const tryAmount = convertCostToTRY(cost);
                                      return (
                                        <tr key={cost.modelCostId}>
                                          <td>
                                            <strong>
                                              {cost.costItemName || "-"}
                                            </strong>
                                          </td>
                                          <td>
                                            <span className="category-badge-small">
                                              {cost.costCategoryName || "-"}
                                            </span>
                                          </td>
                                          <td className="text-center">
                                            {cost.quantity}
                                            {cost.quantity2 &&
                                              ` × ${cost.quantity2}`}
                                          </td>
                                          <td>
                                            {cost.unit}
                                            {cost.unit2 && ` × ${cost.unit2}`}
                                          </td>
                                          <td className="text-center">
                                            {cost.wastagePercentage
                                              ? `%${cost.wastagePercentage}`
                                              : "-"}
                                          </td>
                                          <td className="text-right">
                                            {formatCurrency(
                                              cost.unitPrice || 0,
                                              cost.currency || "TRY",
                                            )}
                                          </td>
                                          <td className="text-right">
                                            <strong>
                                              {formatCurrency(
                                                cost.totalCost || 0,
                                                cost.currency || "TRY",
                                              )}
                                            </strong>
                                          </td>
                                          <td className="text-right">
                                            <span className="try-value">
                                              {formatCurrency(tryAmount, "TRY")}
                                            </span>
                                            {cost.currency !== "TRY" &&
                                              (cost.usdRate ||
                                                cost.eurRate) && (
                                                <div
                                                  style={{
                                                    fontSize: "11px",
                                                    color: "#666",
                                                    marginTop: "4px",
                                                  }}
                                                >
                                                  Kur:{" "}
                                                  {cost.currency === "USD"
                                                    ? cost.usdRate?.toFixed(4)
                                                    : cost.currency === "EUR"
                                                      ? cost.eurRate?.toFixed(4)
                                                      : "-"}{" "}
                                                  TL
                                                </div>
                                              )}
                                          </td>
                                          <td>
                                            {(cost.notes || cost.usage) && (
                                              <div style={{ fontSize: "12px" }}>
                                                {cost.notes && (
                                                  <div
                                                    style={{
                                                      color: "#666",
                                                      fontStyle: "italic",
                                                    }}
                                                  >
                                                    {cost.notes}
                                                  </div>
                                                )}
                                              </div>
                                            )}
                                            {!cost.notes && !cost.usage && (
                                              <span style={{ color: "#999" }}>
                                                -
                                              </span>
                                            )}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          );
                        })}

                        {/* Genel Toplam */}
                        <div className="cost-detail-grand-total">
                          <div className="grand-total-row">
                            <span className="grand-total-label">
                              GENEL TOPLAM
                            </span>
                            <span className="grand-total-value">
                              {formatCurrency(costDetailModal.totalCost, "TRY")}
                            </span>
                          </div>
                          <div
                            className="grand-total-row"
                            style={{
                              background: "#f8f9fe",
                              borderTop: "1px solid #e0e7ff",
                            }}
                          >
                            <span
                              className="grand-total-label"
                              style={{ color: "#667eea" }}
                            >
                              GENEL GİDER DAHİL
                            </span>
                            <span
                              className="grand-total-value"
                              style={{ color: "#667eea" }}
                            >
                              {formatCurrency(
                                costDetailModal.costWithOverhead,
                                "TRY",
                              )}
                            </span>
                          </div>
                          <div
                            className="grand-total-row"
                            style={{
                              background: "#f8f9fe",
                              borderTop: "1px solid #e0e7ff",
                            }}
                          >
                            <span
                              className="grand-total-label"
                              style={{ color: "#764ba2" }}
                            >
                              KAR DAHİL (NİHAİ MALİYET)
                            </span>
                            <span
                              className="grand-total-value"
                              style={{ color: "#764ba2" }}
                            >
                              {formatCurrency(
                                costDetailModal.finalCostWithProfit,
                                "TRY",
                              )}
                            </span>
                          </div>
                          {costDetailModal.quantity > 0 && (
                            <div className="grand-total-row unit">
                              <span className="grand-total-label">
                                BİRİM MALİYET
                              </span>
                              <span className="grand-total-value">
                                {formatCurrency(
                                  costDetailModal.costWithOverhead /
                                    costDetailModal.quantity,
                                  "TRY",
                                )}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </>
              )}
            </div>

            <div className="cost-detail-footer">
              <button className="close-btn" onClick={closeCostDetail}>
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Teknik Seçim Modalı */}
      <TechnicModal
        isOpen={showTechnicModal}
        onClose={() => setShowTechnicModal(false)}
        onSelectTechnic={handleTechnicSelect}
        selectedTechnics={selectedTechnics}
        onClearAll={handleClearTechnics}
      />

      {/* Infinite Scroll Loading Indicator */}
      {isLoadingMore && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "40px 20px",
            gap: "12px",
          }}
        >
          <div
            style={{
              width: "40px",
              height: "40px",
              border: "4px solid #f3f4f6",
              borderTop: "4px solid #667eea",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
            }}
          ></div>
          <span
            style={{
              color: "#667eea",
              fontSize: "16px",
              fontWeight: "500",
            }}
          >
            Daha fazla veri yükleniyor...
          </span>
        </div>
      )}

      {/* No More Data Indicator */}
      {!hasMore && reportData.length > 0 && (
        <div
          style={{
            textAlign: "center",
            padding: "30px 20px",
            color: "#94a3b8",
            fontSize: "14px",
            fontWeight: "500",
            borderTop: "2px solid #f1f5f9",
          }}
        >
          ✓ Tüm kayıtlar yüklendi
        </div>
      )}
      </>
      )}

      {/* Order Costs Modal */}
      <OrderCostsModal
        isOpen={orderCostsModal.isOpen}
        orderId={orderCostsModal.orderId}
        modelId={orderCostsModal.modelId}
        onClose={() =>
          setOrderCostsModal({ isOpen: false, orderId: "", modelId: "" })
        }
      />

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        message={alertModal.message}
        type={alertModal.type}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
      />

      {/* Image Manager Modal */}
      {showImageManager && selectedOrderForImages && (
        <ImageManager
          orderId={selectedOrderForImages}
          onClose={() => {
            setShowImageManager(false);
            setSelectedOrderForImages(null);
          }}
        />
      )}
    </div>
  );
};

export default Reports;
